import React, { useState, useEffect, useRef, useCallback, useMemo, forwardRef, useImperativeHandle } from 'react';
import { createPortal } from 'react-dom';
import './FileExplorer.css';
import fileService from '../../../../services/fileService';
import ContextMenu from './ContextMenu';
import UploadModal from './UploadModal';
import DeleteConfirmModal from './DeleteConfirmModal';
import VideoPlayerModal from './VideoPlayerModal';
import ImagePlayerModal from './ImagePlayerModal';
import DocumentViewerModal from './DocumentViewerModal';
import axios from 'axios';
import { API_BASE_URL } from '../../../../API/api';
import { useIsMobile } from '../../../../hooks/useIsMobile';
import csvIcon from '../../../../assets/csv.png';
import docIcon from '../../../../assets/doc.png';
import pdfIcon from '../../../../assets/pdf.png';
import xmlIcon from '../../../../assets/xml.png';
import { useUserPermissions } from '../../../../hooks/useUserPermissions';
import { pickAvailableName, normalizeNameForCompare, enforceExtension, splitExtension } from '../../../../utils/nameUtils';
import { applyPendingRenames } from '../../../../utils/pendingRenames';
import { buildDeliverableDownloadPrefix, isUnderDeliverables } from '../../../../utils/deliverableDownloadNames';
import { fileExplorerCacheKey } from '../../../../utils/fileExplorerCache';

const DRIVE_AUTO_REFRESH_MS = 5000;
const MARQUEE_DRAG_THRESHOLD_PX = 5;
const FILE_DROPZONE_DRAG_THRESHOLD = 0;

/** Modified-column stamp: locale date + hh:mm. Date alone can't tell two same-day
    uploads apart, which is exactly when you need the column. Seconds stay in the
    row's `title` tooltip so the cell keeps its width. */
function formatModified(value) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '--';
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  })}`;
}

function clientRectsIntersect(a, b) {
  return !(
    a.right < b.left ||
    a.left > b.right ||
    a.bottom < b.top ||
    a.top > b.bottom
  );
}

/** Items in `files` (same order as `.file-list-item` rows) intersecting the marquee in content coordinates. */
function getMarqueeSelectionForBox(el, sx, sy, cx, cy, files) {
  const r = el.getBoundingClientRect();
  const mLeft = Math.min(sx, cx);
  const mTop = Math.min(sy, cy);
  const w = Math.abs(cx - sx);
  const h = Math.abs(cy - sy);
  const marqueeClient = {
    left: r.left + mLeft - el.scrollLeft,
    top: r.top + mTop - el.scrollTop,
    right: r.left + mLeft - el.scrollLeft + w,
    bottom: r.top + mTop - el.scrollTop + h,
  };
  const rows = el.querySelectorAll('.file-list-item');
  const picked = [];
  rows.forEach((node, index) => {
    const item = files[index];
    if (!item) return;
    if (clientRectsIntersect(marqueeClient, node.getBoundingClientRect())) {
      picked.push(item);
    }
  });
  const unique = [...new Map(picked.map((f) => [f.id, f])).values()];
  const last = unique.length ? unique[unique.length - 1] : null;
  const lastIndex = last ? files.findIndex((f) => f.id === last.id) : -1;
  return { unique, lastIndex };
}

/** Match tree folder names case-insensitively (e.g. Source, SOURCE). */
const normalizeExplorerFolderLabel = (name) =>
  String(name ?? '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '');

const signalRsaCacheSync = (projectId) => {
  if (!projectId) return;
  const syncedAt = Date.now();
  try {
    sessionStorage.setItem(`rsaMileageCachesSynced:${projectId}`, String(syncedAt));
  } catch (e) {
    // ignore storage errors
  }
  window.dispatchEvent(new CustomEvent('rsa-mileage-caches-synced', {
    detail: { projectId, syncedAt },
  }));
};

const FileExplorer = forwardRef((
  {
    project,
    onFileSelect,
    onFolderSelect,
    currentPath,
    onBreadcrumbUpdate,
    selectedFolder,
    onClearSelection,
    showUploadModal,
    onOpenUploadModal,
    onCloseUploadModal,
    onFileAdded,
    currentFolderId,
    fileListTopContent = null,
    fileListFilterText = null,
    onFileListFilterTextChange = null,
    showFileListFilter = true,
    // Mobile: icon-only up-navigation in the sortbar row, supplied by the
    // host that owns folder navigation (Deliverables passes both).
    mobileBackAvailable = false,
    onMobileBack = null,
    // Mobile: DOM node to portal the open search toolbar into (the
    // Deliverables breadcrumb pill). Rendering the bar OVER the pill keeps
    // the page height stable — no toolbar row is inserted into the flow.
    mobileSearchPortalNode = null,
    fileListToolbar = null,
    footerExtra = null,
    scopedRootFolder = null,
    hideTreePanel = false,
    onSelectionStateChange = null,
    // Project display name used to prefix downloads of files under the
    // project's Deliverables tree ("<name> SSD Summary 28-41.8.pdf"). Defaults
    // to project.Name; the scoped Deliverables explorer (RSAAnalysis) passes
    // the real project name because its `project.Name` is the scoped folder.
    downloadProjectName = null,
  },
  ref
) => {
  // Mobile = read-only browse/preview/download: single tap opens, no
  // rename/delete/context-menu/marquee, tree panel hidden (CSS).
  const isMobile = useIsMobile();
  const [fileTree, setFileTree] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentFolder, setCurrentFolder] = useState(null);
  const { permissions: userPermissions, loading: permissionsLoading } = useUserPermissions();
  const [selectedFiles, setSelectedFiles] = useState([]); // Array of selected file objects for copy/paste/delete/download operations
  const [lastSelectedIndex, setLastSelectedIndex] = useState(-1);
  const [loading, setLoading] = useState(() => Boolean(project?.Id));
  const [error, setError] = useState(null);
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  // Default: newest first. Most sessions open the explorer to find what was
  // just uploaded/produced, not to scan an alphabetical list.
  const [sortBy, setSortBy] = useState('modified');
  const [sortOrder, setSortOrder] = useState('desc');
  // Fixed column widths based on character count
  const columnWidths = {
    icon: 40,
    name: 'flex', // Name column takes remaining space
    owner: 180, // 200px fixed width
    size: 112,  // 14 characters * 8px per char (10 + 4 more)
    modified: 160, // date + hh:mm (leaves room for a 12-hour locale's AM/PM)
    type: 80    // 10 characters * 8px per char
  };
  
  // Context menu state
  const [contextMenu, setContextMenu] = useState({
    visible: false,
    position: { x: 0, y: 0 }
  });
  const [clipboard, setClipboard] = useState({
    files: [],
    operation: null // 'copy' or 'cut'
  });
  const [showDownloadNotification, setShowDownloadNotification] = useState(false);
  const [isClosingNotification, setIsClosingNotification] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [downloadStatus, setDownloadStatus] = useState(''); // 'creating' or 'downloading'
  const [isMultipleFiles, setIsMultipleFiles] = useState(false);
  const [downloadCancelToken, setDownloadCancelToken] = useState(null);
  const progressIntervalRef = useRef(null);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [selectedVideoFile, setSelectedVideoFile] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [showDocModal, setShowDocModal] = useState(false);
  const [selectedDocFile, setSelectedDocFile] = useState(null);
  const [treePanelWidth, setTreePanelWidth] = useState(350);
  const [driveLoadingFolderId, setDriveLoadingFolderId] = useState(null);
  const [renamingItem, setRenamingItem] = useState(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);  // true while the placeholder row is open
  const [newFolderDraft, setNewFolderDraft] = useState('');
  const newFolderInputRef = useRef(null);
  const [isFileDropzoneActive, setIsFileDropzoneActive] = useState(false);
  const [internalFileListFilterText, setInternalFileListFilterText] = useState('');
  // Mobile: the filter input is collapsed behind a search button until asked
  // for. Desktop always shows the input, so this is never read there.
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  // Mobile long-press selection (the My Files / Drive pattern): hold a row to
  // select it, then plain taps toggle further rows while a selection exists.
  // One shared timer is enough — touch is single-pointer here.
  const longPressTimerRef = useRef(null);
  const longPressFiredRef = useRef(false);
  const touchStartPosRef = useRef({ x: 0, y: 0 });
  const isFileListFilterControlled = fileListFilterText != null;
  const effectiveFileListFilterText = isFileListFilterControlled
    ? fileListFilterText
    : internalFileListFilterText;
  const handleFileListFilterChange = useCallback((value) => {
    if (isFileListFilterControlled) {
      onFileListFilterTextChange?.(value);
      return;
    }
    setInternalFileListFilterText(value);
  }, [isFileListFilterControlled, onFileListFilterTextChange]);

  const renameValidation = useMemo(() => {
    if (!renamingItem) return { ok: true, suggested: null };
    // Validate the name the user will actually get — the extension is hard-locked
    // to the original, so re-attach it before checking for collisions.
    const draft = enforceExtension(renameDraft, renamingItem.name);
    if (!draft) return { ok: true, suggested: null };  // empty handled by commit
    if (draft === renamingItem.name) return { ok: true, suggested: null };  // no-op
    const peers = (currentFolder?.children || []).filter(
      (it) => String(it.id) !== String(renamingItem.id)
    );
    const peerNames = peers.map((p) => p.name);
    const collides = peerNames.some(
      (n) => normalizeNameForCompare(n) === normalizeNameForCompare(draft)
    );
    if (!collides) return { ok: true, suggested: null };
    return {
      ok: false,
      suggested: pickAvailableName(draft, new Set(peerNames)),
    };
  }, [renamingItem, renameDraft, currentFolder?.children]);
  const [pendingUploadFiles, setPendingUploadFiles] = useState([]);

  const fileListRef = useRef(null);
  const treePanelResizeRef = useRef({ startX: 0, startW: 350 });
  const didHandleInitialUrlRef = useRef(false);
  const renameInputRef = useRef(null);
  const skipRenameBlurCommitRef = useRef(false);
  const marqueeStartRef = useRef({ x: 0, y: 0 });
  const marqueeDraggingRef = useRef(false);
  const marqueeJustEndedRef = useRef(false);
  const marqueeListenersRef = useRef({ onMove: null, onUp: null });
  const fileDropzoneDragDepthRef = useRef(0);
  const [marqueeRect, setMarqueeRect] = useState(null);
  // IDs of Drive items that have been optimistically removed but whose delete API call
  // is still in-flight. The auto-refresh poller filters these out so they don't reappear.
  const pendingDriveDeleteIdsRef = useRef(new Set());
  // Drive item id -> the name we applied optimistically and are waiting for Drive to
  // confirm. The auto-refresh poller substitutes these over the names it fetches, so
  // a poll that beats Drive's propagation cannot repaint the old name. Sibling of
  // pendingDriveDeleteIdsRef above, which solves the same race for deletes.
  const pendingDriveRenamesRef = useRef(new Map());
  // Prefetch cache: maps googleDriveId → items[]. Populated on project open so folder
  // navigation is instant (no spinner, no network wait).
  const drivePrefetchCacheRef = useRef(new Map());
  const scopedRootId = String(scopedRootFolder?.id ?? '').trim();
  const scopedRootDriveId = String(
    scopedRootFolder?.googleDriveId
    ?? (scopedRootId.startsWith('gd_') ? scopedRootId.slice(3) : '')
  ).trim();
  const cacheKey = useMemo(() => {
    if (!project?.Id) return null;
    const scopeKey = scopedRootId || (scopedRootDriveId ? `gd_${scopedRootDriveId}` : 'root');
    return fileExplorerCacheKey(project.Id, scopeKey);
  }, [project?.Id, scopedRootDriveId, scopedRootId]);

  const canAccessSourceRoot = useMemo(() => {
    const groups = Array.isArray(userPermissions?.groups) ? userPermissions.groups : [];
    return groups.some((group) => String(group).trim().toLowerCase() === 'admin');
  }, [userPermissions?.groups]);

  const isProjectRootFolder = useCallback((item) => {
    if (!item?.isFolder) {
      return false;
    }
    return item.isRoot || item.level === 0 || String(item.parentId) === String(project?.Id);
  }, [project?.Id]);

  const isRestrictedSourceFolder = useCallback((item, parentFolder = null) => {
    if (!item?.isFolder) {
      return false;
    }
    if (normalizeExplorerFolderLabel(item.name) !== 'SOURCE') {
      return false;
    }
    if (parentFolder) {
      return isProjectRootFolder(parentFolder);
    }
    return isProjectRootFolder(item);
  }, [isProjectRootFolder]);

  const filterTreeByAccess = useCallback((tree, parentFolder = null) => {
    if (!Array.isArray(tree)) {
      return [];
    }
    if (canAccessSourceRoot) {
      return tree;
    }
    const filterNodes = (nodes, parentFolder = null) => nodes.reduce((acc, node) => {
      if (!node) {
        return acc;
      }
      if (isRestrictedSourceFolder(node, parentFolder)) {
        return acc;
      }
      const nextNode = Array.isArray(node.children)
        ? { ...node, children: filterNodes(node.children, node) }
        : node;
      acc.push(nextNode);
      return acc;
    }, []);
    return filterNodes(tree, parentFolder);
  }, [canAccessSourceRoot, isRestrictedSourceFolder]);

  const getDefaultVisibleRoot = useCallback((tree) => {
    const visibleTree = filterTreeByAccess(tree);
    if (visibleTree.length === 0) {
      return null;
    }
    return visibleTree.find((item) => normalizeExplorerFolderLabel(item.name) === 'DELIVERABLES') || visibleTree[0];
  }, [filterTreeByAccess]);

  useEffect(() => {
    return () => {
      const { onMove, onUp } = marqueeListenersRef.current;
      if (onMove) document.removeEventListener('mousemove', onMove);
      if (onUp) document.removeEventListener('mouseup', onUp);
      marqueeListenersRef.current = { onMove: null, onUp: null };
      marqueeDraggingRef.current = false;
      document.body.style.userSelect = '';
    };
  }, []);

  // Update tree: set children for the node with given id (for Google Drive folder contents)
  const setTreeFolderChildren = useCallback((tree, nodeId, newChildren) => {
    return tree.map((node) => {
      if (node.id === nodeId) {
        return {
          ...node,
          children: newChildren,
          driveChildrenLoaded: true,
          hasChildren: newChildren.some((c) => c.isFolder) || newChildren.length > 0
        };
      }
      if (node.children && node.children.length > 0) {
        return { ...node, children: setTreeFolderChildren(node.children, nodeId, newChildren) };
      }
      return node;
    });
  }, []);

  const sortFolderItems = useCallback((items = []) => {
    return [...items].sort((a, b) => {
      if (a.isFolder && !b.isFolder) return -1;
      if (!a.isFolder && b.isFolder) return 1;
      return String(a.name || '').localeCompare(String(b.name || ''));
    });
  }, []);

  const buildDriveItemsSignature = useCallback((items = []) => {
    return items
      .map((item) => [
        String(item?.id ?? ''),
        String(item?.name ?? ''),
        item?.isFolder ? '1' : '0',
        String(item?.size ?? 0),
        String(item?.modified ?? ''),
      ].join('|'))
      .join('::');
  }, []);

  const mergeDriveChildren = useCallback((existingChildren = [], incomingItems = [], level = 0) => {
    const existingById = new Map((existingChildren || []).map((item) => [String(item?.id), item]));
    const merged = (incomingItems || []).map((it) => {
      const incomingId = String(it?.id ?? '');
      const existing = existingById.get(incomingId);
      const base = {
        ...it,
        level,
        expanded: existing?.expanded ?? false,
        children: [],
        hasChildren: Boolean(it?.isFolder),
      };
      if (it?.isFolder && existing) {
        const existingChildrenSafe = Array.isArray(existing.children) ? existing.children : [];
        return {
          ...base,
          children: existingChildrenSafe,
          driveChildrenLoaded: Boolean(existing.driveChildrenLoaded),
          hasChildren: existingChildrenSafe.length > 0 || Boolean(existing.hasChildren) || Boolean(it?.isFolder),
        };
      }
      return base;
    });
    return sortFolderItems(merged);
  }, [sortFolderItems]);

  // Load Google Drive contents for a folder that has googleDriveId; update tree and return loaded children.
  // Checks the prefetch cache first so navigation is instant when the tree was pre-loaded.
  const loadDriveContentsForFolder = useCallback(
    async (folder) => {
      if (!folder?.googleDriveId || folder.driveChildrenLoaded) {
        return folder?.children || [];
      }

      // --- Instant path: serve from prefetch cache (no spinner, no network) ---
      const prefetched = drivePrefetchCacheRef.current.get(folder.googleDriveId);
      if (prefetched) {
        const level = (folder.level ?? 0) + 1;
        const formatted = prefetched.map((it) => ({
          ...it,
          level,
          children: [],
          hasChildren: it.isFolder,
          expanded: false,
        }));
        const visibleChildren = filterTreeByAccess(formatted, folder);
        setFileTree((prev) => setTreeFolderChildren(prev, folder.id, visibleChildren));
        return visibleChildren;
      }

      // --- Fallback: live fetch with loading spinner ---
      setDriveLoadingFolderId(folder.id);
      try {
        const items = await fileService.getGoogleDriveContents(project?.Id, folder.googleDriveId);
        const level = (folder.level ?? 0) + 1;
        const formatted = items.map((it) => ({
          ...it,
          level,
          children: [],
          hasChildren: it.isFolder,
          expanded: false
        }));
        const visibleChildren = filterTreeByAccess(formatted, folder);
        setFileTree((prev) => setTreeFolderChildren(prev, folder.id, visibleChildren));
        return visibleChildren;
      } catch (err) {
        console.error('Error loading Google Drive contents:', err);
        return [];
      } finally {
        setDriveLoadingFolderId(null);
      }
    },
    [filterTreeByAccess, project?.Id, setTreeFolderChildren]
  );

  // Define functions before useEffects that use them
  const loadProjectFiles = useCallback(async (forceRefresh = false, options = {}) => {
    if (!project?.Id) {
      setLoading(false);
      return;
    }
    const activeCacheKey = cacheKey;
    if (!activeCacheKey) {
      setLoading(false);
      return;
    }

    const maybeHydrateRootDriveName = async (tree) => {
      if (!Array.isArray(tree) || tree.length === 0 || !tree[0]?.googleDriveId) {
        return tree;
      }
      try {
        const meta = await fileService.getGoogleDriveFolderMeta(project.Id, tree[0].googleDriveId);
        if (meta?.name && meta.name !== tree[0].name) {
          const next = [...tree];
          next[0] = { ...next[0], name: meta.name };
          return next;
        }
      } catch (e) {
        console.warn('Could not sync root folder name from Google Drive:', e);
      }
      return tree;
    };

    const findInNodes = (items, targetId) => {
      for (const item of items || []) {
        if (String(item?.id) === String(targetId)) {
          return item;
        }
        if (item.children?.length > 0) {
          const found = findInNodes(item.children, targetId);
          if (found) return found;
        }
      }
      return null;
    };

    const buildSyntheticScopedRoot = async () => {
      const syntheticId = scopedRootId || (scopedRootDriveId ? `gd_${scopedRootDriveId}` : '');
      if (!syntheticId) return null;

      let scopedName = String(scopedRootFolder?.name || '').trim() || 'Scoped Folder';
      if (scopedRootDriveId) {
        try {
          const meta = await fileService.getGoogleDriveFolderMeta(project.Id, scopedRootDriveId);
          if (meta?.name) {
            scopedName = meta.name;
          }
        } catch (e) {
          console.warn('Could not hydrate scoped root folder name from Google Drive:', e);
        }
      }

      return {
        id: syntheticId,
        name: scopedName,
        isFolder: true,
        size: 0,
        projectId: project.Id,
        parentId: project.Id,
        ownerId: null,
        ownerName: '',
        isDeleted: false,
        modified: new Date(),
        created: new Date(),
        googleDriveId: scopedRootDriveId || null,
        isGoogleDriveItem: Boolean(scopedRootDriveId),
        hasChildren: Boolean(scopedRootDriveId),
        children: [],
        level: 0,
        expanded: false,
      };
    };

    const maybeScopeTree = async (tree) => {
      if (!scopedRootId && !scopedRootDriveId) {
        return tree;
      }

      const targetId = scopedRootId || (scopedRootDriveId ? `gd_${scopedRootDriveId}` : '');
      if (targetId) {
        const found = findInNodes(tree, targetId);
        if (found) {
          return [{ ...found }];
        }
      }

      const syntheticRoot = await buildSyntheticScopedRoot();
      return syntheticRoot ? [syntheticRoot] : tree;
    };

    if (!forceRefresh) {
      try {
        const cachedRaw = sessionStorage.getItem(activeCacheKey);
        if (cachedRaw) {
          const cached = JSON.parse(cachedRaw);
          if (Array.isArray(cached?.fileTree) && cached.fileTree.length > 0) {
            const cachedTree = filterTreeByAccess(cached.fileTree);
            setError(null);
            if (cachedTree.length === 0) {
              setFileTree([]);
              setExpandedFolders(new Set());
              setCurrentFolder(null);
              setLoading(false);
              return;
            }
            const hydratedTree = await maybeHydrateRootDriveName(cachedTree);
            setFileTree(hydratedTree);

            const defaultRoot = getDefaultVisibleRoot(hydratedTree) || hydratedTree[0];
            const expandedIds = Array.isArray(cached?.expandedFolderIds) && cached.expandedFolderIds.length > 0
              ? cached.expandedFolderIds
              : [defaultRoot.id];
            setExpandedFolders(new Set(expandedIds));

            const folderFromCache = cached.currentFolderId
              ? findInNodes(hydratedTree, cached.currentFolderId)
              : null;
            setCurrentFolder(folderFromCache || defaultRoot);
            setLoading(false);
            return;
          }
        }
      } catch (cacheError) {
        console.warn('Failed to read File Explorer cache, falling back to API fetch:', cacheError);
        sessionStorage.removeItem(activeCacheKey);
      }
    }

    try {
      setLoading(true);
      setError(null);

      console.log('Loading files for project:', project.Id);
      const files = await fileService.getProjectFiles(project.Id);
      const tree = fileService.buildFileTree(files, project.Id);
      const hydratedTree = await maybeHydrateRootDriveName(tree);
      const scopedTree = filterTreeByAccess(await maybeScopeTree(hydratedTree));

      // Collect Drive-linked root folder IDs immediately after building the MySQL tree.
      // We kick off the prefetch RIGHT NOW (non-blocking) so it runs in parallel while
      // React renders the tree. By the time the user can click anything, the cache is ready.
      const rootDriveIds = [];
      const collectDriveRoots = (items) => {
        for (const item of items) {
          if (item.isFolder && item.googleDriveId) rootDriveIds.push(item.googleDriveId);
          if (item.children?.length > 0) collectDriveRoots(item.children);
        }
      };
      collectDriveRoots(scopedTree);

      if (rootDriveIds.length > 0) {
        drivePrefetchCacheRef.current.clear();
        // Fire prefetch in the background — do NOT await it so the tree renders immediately.
        fileService.prefetchDriveTree(project.Id, rootDriveIds).then((folders) => {
          if (!folders || Object.keys(folders).length === 0) return;

          // Populate the in-memory cache so loadDriveContentsForFolder is instant.
          Object.entries(folders).forEach(([driveId, items]) => {
            drivePrefetchCacheRef.current.set(driveId, items);
          });

          // Also patch the live tree: inject children + mark driveChildrenLoaded on every
          // Drive-linked node so the spinner condition is never triggered.
          const injectChildren = (nodes, level = 0) =>
            nodes.map((node) => {
              const patched = { ...node };
              if (node.isFolder && node.googleDriveId && folders[node.googleDriveId]) {
                const childLevel = level + 1;
                const children = folders[node.googleDriveId].map((it) => ({
                  ...it,
                  level: childLevel,
                  children: [],
                  hasChildren: it.isFolder,
                  expanded: false,
                  // Recursively inject grandchildren if prefetched
                  ...(it.isFolder && folders[it.googleDriveId]
                    ? {
                        children: injectChildren(
                          folders[it.googleDriveId].map((c) => ({ ...c, level: childLevel + 1, children: [], hasChildren: c.isFolder, expanded: false })),
                          childLevel + 1
                        ),
                        driveChildrenLoaded: true,
                        hasChildren: folders[it.googleDriveId].length > 0,
                      }
                    : {}),
                }));
                patched.children = filterTreeByAccess(children, node);
                patched.driveChildrenLoaded = true;
                patched.hasChildren = patched.children.length > 0;
              }
              if (patched.children?.length > 0) {
                patched.children = injectChildren(patched.children, level + 1);
              }
              return patched;
            });

          setFileTree((prev) => injectChildren(prev));
        }).catch(() => {
          // Non-fatal — lazy loading with spinner still works as fallback.
        });
      }

      setFileTree(scopedTree);

      // Restore previous/current folder when possible, otherwise default to root folder.
      if (scopedTree.length > 0) {
        const preserveFolderId = options?.preserveFolderId;
        const defaultRoot = getDefaultVisibleRoot(scopedTree) || scopedTree[0];

        const nextFolder = preserveFolderId != null
          ? findInNodes(scopedTree, preserveFolderId) || defaultRoot
          : defaultRoot;

        setCurrentFolder(nextFolder);
        setExpandedFolders(new Set([defaultRoot.id, nextFolder.id]));
      } else {
        setCurrentFolder(null);
        setExpandedFolders(new Set());
      }

    } catch (err) {
      console.error('Error loading project files:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [
    cacheKey,
    filterTreeByAccess,
    getDefaultVisibleRoot,
    project?.Id,
    scopedRootDriveId,
    scopedRootFolder?.name,
    scopedRootId
  ]);

  // Keep session cache in sync so switching tabs does not refetch/reload every time.
  useEffect(() => {
    if (!project?.Id || fileTree.length === 0) return;
    if (!cacheKey) return;
    try {
      const payload = {
        fileTree,
        currentFolderId: currentFolder?.id || null,
        expandedFolderIds: Array.from(expandedFolders),
        cachedAt: Date.now()
      };
      sessionStorage.setItem(cacheKey, JSON.stringify(payload));
    } catch (cacheError) {
      console.warn('Failed to persist File Explorer cache:', cacheError);
    }
  }, [cacheKey, fileTree, currentFolder?.id, expandedFolders, project?.Id]);

  const findFolderById = useCallback((id) => {
    const findInTree = (items) => {
      for (const item of items) {
        if (item.id === id) return item;
        if (item.children) {
          const found = findInTree(item.children);
          if (found) return found;
        }
      }
      return null;
    };
    return findInTree(fileTree);
  }, [fileTree]);

  // Add a freshly uploaded file to the tree/currentFolder without a full reload.
  // Handles both S3 responses (raw DB fields: isfolder, projects_id, …) and
  // Google Drive responses (already uses camelCase frontend fields).
  const handleFileAdded = useCallback((rawFile) => {
    let fileNode;
    if (rawFile.isfolder !== undefined) {
      // S3 upload — backend returns raw DB row; normalise it
      fileNode = fileService.formatFileForFrontend(rawFile);
    } else {
      // Drive upload — already in frontend shape
      fileNode = { ...rawFile };
    }

    // Ensure required tree-node defaults are present
    fileNode = {
      children: [],
      hasChildren: false,
      expanded: false,
      level: 0,
      ...fileNode,
    };

    const parentId = fileNode.parentId;
    // Drive file parentIds look like "gd_<driveId>"
    const driveParentId =
      typeof parentId === 'string' && parentId.startsWith('gd_')
        ? parentId.slice(3)
        : null;

    const matchesParent = (item) =>
      String(item.id) === String(parentId) ||
      (driveParentId != null && item.googleDriveId === driveParentId);

    const insertIntoTree = (items) =>
      items.map((item) => {
        if (matchesParent(item)) {
          return {
            ...item,
            hasChildren: true,
            children: [
              ...(item.children || []),
              { ...fileNode, level: (item.level ?? 0) + 1 },
            ],
          };
        }
        if (item.children?.length) {
          return { ...item, children: insertIntoTree(item.children) };
        }
        return item;
      });

    setFileTree((prev) => insertIntoTree(prev));

    setCurrentFolder((prev) => {
      if (!prev || !matchesParent(prev)) return prev;
      return {
        ...prev,
        hasChildren: true,
        children: [
          ...(prev.children || []),
          { ...fileNode, level: (prev.level ?? 0) + 1 },
        ],
      };
    });

    // Forward to any external callback the parent may have provided
    if (onFileAdded) onFileAdded(rawFile);
  }, [onFileAdded]);

  const rootBreadcrumbSegment = useCallback(() => {
    if (fileTree.length > 0) {
      return {
        id: fileTree[0].id,
        name: fileTree[0].name || project?.Name || 'Project Files',
      };
    }
    return { id: 'root', name: project?.Name || 'Project Files' };
  }, [fileTree, project?.Name]);

  // Project rename (ProjectView header): relabel the root node in place, or the
  // breadcrumb rebuilt from it on the next folder navigation shows the old name.
  const renameRootFolder = useCallback((newName) => {
    if (!newName || scopedRootId || scopedRootDriveId || fileTree.length === 0) return;
    const rootId = fileTree[0].id;
    setFileTree((prev) => (
      prev[0]?.id === rootId ? [{ ...prev[0], name: newName }, ...prev.slice(1)] : prev
    ));
    setCurrentFolder((prev) => (prev?.id === rootId ? { ...prev, name: newName } : prev));
  }, [fileTree, scopedRootId, scopedRootDriveId]);

  const buildFolderPath = useCallback((folder) => {
    const rootSeg = rootBreadcrumbSegment();

    if (!folder) {
      return [rootSeg];
    }

    const findPath = (items, targetId, currentPath = []) => {
      for (const item of items) {
        const newPath = [...currentPath, { id: item.id, name: item.name }];
        if (item.id === targetId) {
          return newPath;
        }
        if (item.children) {
          const found = findPath(item.children, targetId, newPath);
          if (found) return found;
        }
      }
      return null;
    };

    const folderPath = findPath(fileTree, folder.id);
    if (folderPath) {
      return folderPath;
    }

    return [rootSeg];
  }, [fileTree, rootBreadcrumbSegment]);

  // Names of the folders above `itemId` in the tree (root first, item excluded),
  // or null when the item is not in the tree (e.g. Drive children shown in the
  // list before the tree node was refreshed).
  const findItemAncestorNames = useCallback((itemId) => {
    const walk = (items, trail) => {
      for (const node of items || []) {
        if (String(node?.id) === String(itemId)) return trail;
        if (Array.isArray(node?.children) && node.children.length > 0) {
          const found = walk(node.children, [...trail, node.name]);
          if (found) return found;
        }
      }
      return null;
    };
    return walk(fileTree, []);
  }, [fileTree]);

  // `name_prefix` for a download of `items` (double-click, selection, download-all):
  // the project display name when EVERY item lives under the project's
  // Deliverables folder (any depth), '' otherwise. An item's location is its
  // ancestor chain in the tree; when the item is not in the tree yet it is
  // assumed to sit in the current folder (every download entry point acts on
  // the current folder's list), so the current folder's breadcrumb is used.
  const resolveDownloadNamePrefix = useCallback((items) => {
    const projectName = String(downloadProjectName ?? project?.Name ?? '').trim();
    const list = (Array.isArray(items) ? items : []).filter(Boolean);
    if (!projectName || list.length === 0) return '';
    const currentFolderSegments = buildFolderPath(currentFolder).map((segment) => segment?.name);
    const pathFor = (item) => findItemAncestorNames(item?.id) ?? currentFolderSegments;
    if (!list.every((item) => isUnderDeliverables(pathFor(item)))) return '';
    return buildDeliverableDownloadPrefix({ projectName, pathSegments: pathFor(list[0]) });
  }, [downloadProjectName, project?.Name, buildFolderPath, currentFolder, findItemAncestorNames]);

  const isFolderUnderOrIsSource = useCallback(
    (item) => {
      if (!item?.isFolder) {
        return false;
      }
      const node = findFolderById(item.id) ?? item;
      const segments = buildFolderPath(node);
      const sourceIdx = segments.findIndex((s) => normalizeExplorerFolderLabel(s.name) === 'SOURCE');
      if (sourceIdx < 0) {
        return false;
      }
      return segments.length - 1 >= sourceIdx;
    },
    [buildFolderPath, findFolderById]
  );

  const selectionContainsSourceSubtreeFolder = useCallback(
    (files) => (files || []).some((f) => isFolderUnderOrIsSource(f)),
    [isFolderUnderOrIsSource]
  );

  const deleteBlockedForSourceSubtreeFolders = useMemo(
    () => selectionContainsSourceSubtreeFolder(selectedFiles),
    [selectedFiles, selectionContainsSourceSubtreeFolder]
  );

  useEffect(() => {
    if (typeof onSelectionStateChange !== 'function') {
      return;
    }
    onSelectionStateChange({
      selectedFiles,
      selectedCount: selectedFiles.length,
      hasSelectedFiles: selectedFiles.length > 0,
      canDeleteSelection: selectedFiles.length > 0 && !deleteBlockedForSourceSubtreeFolders,
    });
  }, [deleteBlockedForSourceSubtreeFolders, onSelectionStateChange, selectedFiles]);

  useEffect(() => {
    if (project?.Id && !permissionsLoading) {
      loadProjectFiles();

      // Push initial project URL to browser history if not already there
      const currentPath = window.location.pathname;
      const expectedPath = `/projects/${project.Id}`;

      if (currentPath !== expectedPath) {
        const state = { folderId: 'root' };
        window.history.pushState(state, '', expectedPath);
      }
    }
  }, [project?.Id, loadProjectFiles, permissionsLoading]);


  useEffect(() => {
    didHandleInitialUrlRef.current = false;
  }, [project?.Id]);

  useEffect(() => {
    if (!renamingItem) {
      return;
    }

    const frameId = window.requestAnimationFrame(() => {
      if (renameInputRef.current) {
        renameInputRef.current.focus();
        // Windows-Explorer behavior: highlight only the base name, leaving the
        // extension unselected so the user's first keystroke can't clobber it.
        const value = renameInputRef.current.value || '';
        const { stem } = splitExtension(value);
        const baseLength = renamingItem?.isFolder ? value.length : stem.length;
        renameInputRef.current.setSelectionRange(0, baseLength);
      }
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [renamingItem]);

  // Handle initial URL parameters
  useEffect(() => {
    if (didHandleInitialUrlRef.current || fileTree.length === 0) {
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const folderId = urlParams.get('folder');
    
    // Also check for folder ID in the path (e.g., /projects/2746/268002)
    const pathParts = window.location.pathname.split('/');
    const pathFolderId = pathParts[pathParts.length - 1];
    
    const targetFolderId = folderId || pathFolderId;

    let nextFolder = null;
    let nextPath = null;

    if (!targetFolderId || targetFolderId === 'root' || targetFolderId === String(project?.Id)) {
      nextFolder = fileTree[0] || null;
      nextPath = fileTree.length > 0
        ? [{ id: fileTree[0].id, name: fileTree[0].name || project?.Name || 'Project Files' }]
        : [{ id: 'root', name: project?.Name || 'Project Files' }];
    } else {
      const targetFolder = findFolderById(targetFolderId);
      if (!targetFolder) {
        return;
      }
      nextFolder = targetFolder;
      nextPath = buildFolderPath(targetFolder);
    }

    if (nextFolder) {
      setCurrentFolder((prev) => (prev?.id === nextFolder.id ? prev : nextFolder));
    }

    if (onBreadcrumbUpdate && Array.isArray(nextPath)) {
      const samePath = Array.isArray(currentPath)
        && currentPath.length === nextPath.length
        && currentPath.every((item, index) => (
          item?.id === nextPath[index]?.id && item?.name === nextPath[index]?.name
        ));
      if (!samePath) {
        onBreadcrumbUpdate(nextPath);
      }
    }

    didHandleInitialUrlRef.current = true;
  }, [fileTree, project?.Id, project?.Name, currentPath, onBreadcrumbUpdate, findFolderById, buildFolderPath]);

  // Handle breadcrumb navigation
  useEffect(() => {
    if (selectedFolder === null) {
      // Navigate to root
      if (fileTree.length > 0) {
        setCurrentFolder(fileTree[0]);
      }
    } else if (selectedFolder && selectedFolder.id !== 'root') {
      // Already showing this folder? Don't re-navigate: after a folder open,
      // onFolderSelect echoes the selection back as this prop, and the tree
      // copy found by findFolderById has NO freshly-fetched Drive children —
      // re-setting it clobbered the loaded listing ("This folder is empty")
      // and double-pushed the breadcrumb path (scoped Deliverables tab).
      if (String(currentFolder?.id) === String(selectedFolder.id)) {
        return;
      }
      // Navigate to specific folder
      const targetFolder = findFolderById(selectedFolder.id);
      if (targetFolder) {
        setCurrentFolder(targetFolder);
        if (onBreadcrumbUpdate) {
          onBreadcrumbUpdate(buildFolderPath(targetFolder));
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFolder, fileTree, findFolderById, onBreadcrumbUpdate, buildFolderPath]);

  useEffect(() => {
    if (canAccessSourceRoot || !currentFolder) {
      return;
    }
    const currentPathSegments = buildFolderPath(currentFolder);
    const isRestrictedCurrentFolder =
      normalizeExplorerFolderLabel(currentFolder.name) === 'SOURCE'
      && currentPathSegments.length <= 2;
    if (!isRestrictedCurrentFolder) {
      return;
    }
    const fallbackFolder = getDefaultVisibleRoot(fileTree);
    if (!fallbackFolder) {
      setCurrentFolder(null);
      return;
    }
    setCurrentFolder((prev) => (prev?.id === fallbackFolder.id ? prev : fallbackFolder));
    if (onBreadcrumbUpdate) {
      onBreadcrumbUpdate(buildFolderPath(fallbackFolder));
    }
  }, [
    buildFolderPath,
    canAccessSourceRoot,
    currentFolder,
    fileTree,
    getDefaultVisibleRoot,
    onBreadcrumbUpdate,
  ]);

  // Auto-load Drive children for the currently displayed folder on first render/navigation.
  // This keeps file-list-content fresh without requiring a tree click.
  useEffect(() => {
    let cancelled = false;

    const hydrateCurrentDriveFolder = async () => {
      if (!currentFolder?.googleDriveId || currentFolder.driveChildrenLoaded) {
        return;
      }

      const folderSnapshot = currentFolder;
      const children = await loadDriveContentsForFolder(folderSnapshot);
      if (cancelled) {
        return;
      }

      setCurrentFolder((prev) => {
        if (!prev || prev.id !== folderSnapshot.id) {
          return prev;
        }
        return {
          ...prev,
          children,
          driveChildrenLoaded: true,
          hasChildren: children.length > 0
        };
      });
    };

    hydrateCurrentDriveFolder();
    return () => {
      cancelled = true;
    };
  }, [currentFolder, loadDriveContentsForFolder]);

  // Auto-refresh current Google Drive folder so newly added files/folders appear without manual refresh.
  useEffect(() => {
    if (!project?.Id || !currentFolder?.googleDriveId) {
      return;
    }

    let cancelled = false;
    const folderIdSnapshot = currentFolder.id;
    const driveIdSnapshot = currentFolder.googleDriveId;

    const refreshCurrentDriveFolder = async () => {
      if (cancelled || document.hidden) return;
      try {
        let items = await fileService.getGoogleDriveContents(project.Id, driveIdSnapshot);
        if (cancelled) return;

        // Filter out any items whose delete is still in-flight so they don't reappear.
        if (pendingDriveDeleteIdsRef.current.size > 0) {
          items = items.filter((it) => !pendingDriveDeleteIdsRef.current.has(it.id));
        }

        // Substitute names for renames Drive has not caught up on yet, so a poll
        // that beats propagation cannot repaint the old name. Runs BEFORE the
        // signature comparison below, so a fetch differing only by a stale name
        // now compares equal and is skipped entirely.
        items = applyPendingRenames(items, pendingDriveRenamesRef.current);

        const level = (currentFolder.level ?? 0) + 1;
        const existingChildren = Array.isArray(currentFolder.children) ? currentFolder.children : [];
        const mergedChildren = mergeDriveChildren(existingChildren, items, level);
        const visibleChildren = filterTreeByAccess(mergedChildren, currentFolder);
        const prevSig = buildDriveItemsSignature(existingChildren);
        const nextSig = buildDriveItemsSignature(visibleChildren);
        if (prevSig === nextSig) {
          return;
        }

        setFileTree((prevTree) => setTreeFolderChildren(prevTree, folderIdSnapshot, visibleChildren));
        setCurrentFolder((prev) => {
          if (!prev || prev.id !== folderIdSnapshot) {
            return prev;
          }
          return {
            ...prev,
            children: visibleChildren,
            driveChildrenLoaded: true,
            hasChildren: visibleChildren.length > 0,
          };
        });
      } catch (err) {
        console.warn('Drive auto-refresh failed for folder:', driveIdSnapshot, err);
      }
    };

    // First refresh quickly after folder selection, then keep polling.
    const firstTick = window.setTimeout(refreshCurrentDriveFolder, 1200);
    const intervalId = window.setInterval(refreshCurrentDriveFolder, DRIVE_AUTO_REFRESH_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(firstTick);
      window.clearInterval(intervalId);
    };
  }, [
    project?.Id,
    currentFolder?.id,
    currentFolder?.googleDriveId,
    currentFolder?.level,
    currentFolder?.children,
    mergeDriveChildren,
    buildDriveItemsSignature,
    filterTreeByAccess,
    setTreeFolderChildren,
  ]);

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = (event) => {
      // Check URL parameters first
      const urlParams = new URLSearchParams(window.location.search);
      const folderId = urlParams.get('folder');
      
      // Also check for folder ID in the path
      const pathParts = window.location.pathname.split('/');
      const pathFolderId = pathParts[pathParts.length - 1];
      
      const targetFolderId = folderId || pathFolderId;
      
      if (targetFolderId && fileTree.length > 0) {
        if (targetFolderId === 'root') {
          setCurrentFolder(fileTree.length > 0 ? fileTree[0] : null);
          if (onBreadcrumbUpdate) {
            onBreadcrumbUpdate(
              fileTree.length > 0
                ? [{ id: fileTree[0].id, name: fileTree[0].name || project?.Name || 'Project Files' }]
                : [{ id: 'root', name: project?.Name || 'Project Files' }]
            );
          }
        } else {
          const targetFolder = findFolderById(targetFolderId);
          if (targetFolder) {
            setCurrentFolder(targetFolder);
            // Update breadcrumb to match the folder path
            const folderPath = buildFolderPath(targetFolder);
            if (onBreadcrumbUpdate) {
              onBreadcrumbUpdate(folderPath);
            }
          }
        }
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [fileTree, project, onBreadcrumbUpdate, findFolderById, buildFolderPath]);

  const toggleFolder = (folderId) => {
    setExpandedFolders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(folderId)) {
        newSet.delete(folderId);
      } else {
        newSet.add(folderId);
      }
      return newSet;
    });
  };

  const handleExpandWithDrive = useCallback(
    async (item) => {
      if (item.googleDriveId && !item.driveChildrenLoaded) {
        await loadDriveContentsForFolder(item);
      }
      toggleFolder(item.id);
    },
    [loadDriveContentsForFolder]
  );

  const handleTreeFolderClick = useCallback(
    async (folder) => {
      if (folder.googleDriveId && !folder.driveChildrenLoaded) {
        const children = await loadDriveContentsForFolder(folder);
        setCurrentFolder({ ...folder, children, driveChildrenLoaded: true, hasChildren: children.length > 0 });
      } else {
        setCurrentFolder(folder);
      }
      const newPath = buildFolderPath(folder);
      if (onBreadcrumbUpdate) {
        onBreadcrumbUpdate(newPath);
      }
      setSelectedFiles([]);
      if (onFolderSelect) {
        onFolderSelect(folder);
      }
      const state = { folderId: folder.id };
      const existingParams = new URLSearchParams(window.location.search);
      existingParams.set('folder', folder.id);
      const url = `/projects/${project.Id}?${existingParams.toString()}`;
      window.history.pushState(state, '', url);
    },
    [project?.Id, project?.Name, onBreadcrumbUpdate, onFolderSelect, loadDriveContentsForFolder, buildFolderPath]
  );

  const handleFileListDoubleClick = async (item) => {
    if (item.isFolder) {
      let folderToSet = item;
      if (item.isGoogleDriveItem && item.googleDriveId) {
        setDriveLoadingFolderId(item.id);
        try {
          const children = await fileService.getGoogleDriveContents(project?.Id, item.googleDriveId);
          const level = (item.level ?? 0) + 1;
          folderToSet = {
            ...item,
            children: filterTreeByAccess(
              children.map((it) => ({ ...it, level, children: [], hasChildren: it.isFolder })),
              item
            ),
            driveChildrenLoaded: true
          };
        } catch (err) {
          console.error('Error loading Google Drive folder:', err);
        } finally {
          setDriveLoadingFolderId(null);
        }
      }
      setCurrentFolder(folderToSet);
      const builtPath = buildFolderPath(folderToSet);
      const pathIncludesTarget =
        Array.isArray(builtPath) &&
        builtPath.some((segment) => String(segment?.id) === String(folderToSet.id));
      const newPath = pathIncludesTarget
        ? builtPath
        : (Array.isArray(currentPath) && currentPath.length > 0
            ? [...currentPath, { id: item.id, name: item.name }]
            : builtPath);
      if (onBreadcrumbUpdate) {
        onBreadcrumbUpdate(newPath);
      }
      if (onFolderSelect) {
        onFolderSelect(folderToSet);
      }
      setSelectedFiles([]);
      const state = { folderId: item.id };
      const existingParams = new URLSearchParams(window.location.search);
      existingParams.set('folder', item.id);
      const url = `/projects/${project.Id}?${existingParams.toString()}`;
      window.history.pushState(state, '', url);
    } else {
      const fileName = String(item.name || '');
      const lastDotIndex = fileName.lastIndexOf('.');
      const ext = lastDotIndex > 0 ? fileName.slice(lastDotIndex + 1).toLowerCase() : '';

      if (ext === 'mp4') {
        setSelectedVideoFile(item);
        setShowVideoModal(true);
      } else if (ext === 'csv' || ext === 'docx' || ext === 'xml' || ext === 'pdf') {
        setSelectedDocFile(item);
        setShowDocModal(true);
      } else {
        try {
          const downloadOptions = { namePrefix: resolveDownloadNamePrefix([item]) };
          if (item.isGoogleDriveItem && item.googleDriveId) {
            await fileService.downloadGoogleDriveFile(item.googleDriveId, item.name, null, downloadOptions);
          } else {
            await fileService.downloadFiles([item], null, null, downloadOptions);
          }
        } catch (err) {
          console.error('Error downloading file on double-click:', err);
        }
      }
    }
  };

  const handleFileListClick = (item, index, event) => {
    event.preventDefault();
    event.stopPropagation(); // Prevent event from bubbling to container

    if (marqueeJustEndedRef.current) {
      marqueeJustEndedRef.current = false;
      return;
    }
    
    // Prevent text selection during multi-selection
    if (event.ctrlKey || event.shiftKey) {
      event.preventDefault();
      window.getSelection().removeAllRanges();
    }
    
    // Handle checkbox toggle logic for the entire row
    const isSelected = selectedFiles.some(file => file.id === item.id);
    
    if (event.ctrlKey) {
      // Ctrl+Click: Toggle selection
      if (isSelected) {
        setSelectedFiles(selectedFiles.filter(file => file.id !== item.id));
      } else {
        setSelectedFiles([...selectedFiles, item]);
      }
      setLastSelectedIndex(index);
    } else if (event.shiftKey && lastSelectedIndex !== -1) {
      // Shift+Click: Range selection
      const start = Math.min(lastSelectedIndex, index);
      const end = Math.max(lastSelectedIndex, index);
      const currentFiles = getCurrentFiles();
      const rangeFiles = currentFiles.slice(start, end + 1);
      
      // Merge with existing selection, avoiding duplicates
      const existingIds = new Set(selectedFiles.map(file => file.id));
      const newFiles = rangeFiles.filter(file => !existingIds.has(file.id));
      setSelectedFiles([...selectedFiles, ...newFiles]);
    } else {
      // Regular click: Select only the clicked item
      setSelectedFiles([item]);
      setLastSelectedIndex(index);
    }
  };

  // Handle clicking on empty areas to deselect all files
  const handleFileListContentClick = (event) => {
    if (marqueeJustEndedRef.current) {
      marqueeJustEndedRef.current = false;
      return;
    }
    // Only deselect if clicking on the container itself, not on file items
    if (event.target === event.currentTarget) {
      setSelectedFiles([]);
      setLastSelectedIndex(-1);
    }
  };

  const dragEventContainsFiles = useCallback((event) => {
    const types = event?.dataTransfer?.types;
    if (!types) return false;
    return Array.from(types).includes('Files');
  }, []);

  const extractDroppedFiles = useCallback((dataTransfer) => {
    if (!dataTransfer) return [];
    const filesFromList = Array.from(dataTransfer.files || []);
    if (filesFromList.length > 0) return filesFromList;
    const filesFromItems = Array.from(dataTransfer.items || [])
      .map((item) => (item?.kind === 'file' ? item.getAsFile() : null))
      .filter(Boolean);
    return filesFromItems;
  }, []);

  const handleFileListDragEnter = useCallback((event) => {
    if (!dragEventContainsFiles(event)) return;
    event.preventDefault();
    event.stopPropagation();
    fileDropzoneDragDepthRef.current += 1;
    if (fileDropzoneDragDepthRef.current >= FILE_DROPZONE_DRAG_THRESHOLD) {
      setIsFileDropzoneActive(true);
    }
  }, [dragEventContainsFiles]);

  const handleFileListDragOver = useCallback((event) => {
    if (!dragEventContainsFiles(event)) return;
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
    if (!isFileDropzoneActive) {
      setIsFileDropzoneActive(true);
    }
  }, [dragEventContainsFiles, isFileDropzoneActive]);

  const handleFileListDragLeave = useCallback((event) => {
    if (!dragEventContainsFiles(event)) return;
    event.preventDefault();
    event.stopPropagation();
    fileDropzoneDragDepthRef.current = Math.max(0, fileDropzoneDragDepthRef.current - 1);
    if (fileDropzoneDragDepthRef.current === 0) {
      setIsFileDropzoneActive(false);
    }
  }, [dragEventContainsFiles]);

  const handleFileListDrop = useCallback((event) => {
    if (!dragEventContainsFiles(event)) return;
    event.preventDefault();
    event.stopPropagation();
    fileDropzoneDragDepthRef.current = 0;
    setIsFileDropzoneActive(false);

    const droppedFiles = extractDroppedFiles(event.dataTransfer);
    if (droppedFiles.length === 0) return;

    setPendingUploadFiles(droppedFiles);
    if (typeof onOpenUploadModal === 'function') {
      onOpenUploadModal();
    }
  }, [dragEventContainsFiles, extractDroppedFiles, onOpenUploadModal]);

  const getFileListContentRelativeCoords = useCallback((e) => {
    const el = fileListRef.current;
    if (!el) return { x: 0, y: 0 };
    const r = el.getBoundingClientRect();
    return {
      x: e.clientX - r.left + el.scrollLeft,
      y: e.clientY - r.top + el.scrollTop,
    };
  }, []);

  // Clear selection function that can be called from parent components
  const clearSelection = useCallback(() => {
    setSelectedFiles([]);
    setLastSelectedIndex(-1);
    // Call parent's clear selection if provided
    if (onClearSelection) {
      onClearSelection();
    }
  }, [onClearSelection]);

  // Utility functions for working with selected files
  // Note: Some utility functions are kept for potential future use
  // const getSelectedFiles = () => selectedFiles;
  // const getSelectedFileIds = () => selectedFiles.map(file => file.id);
  // const isFileSelected = (fileId) => selectedFiles.some(file => file.id === fileId);
  // const hasSelection = () => selectedFiles.length > 0;
  
  const getCurrentFiles = useCallback(() => {
    if (!currentFolder) return [];
    
    const visibleChildren = Array.isArray(currentFolder.children)
      ? filterTreeByAccess(currentFolder.children, currentFolder)
      : [];
    const normalizedFilterText = String(effectiveFileListFilterText ?? '').trim().toLowerCase();
    const allItems = [];
    
    // Add current folder's children (both files and folders)
    allItems.push(
      ...visibleChildren.filter((item) => {
        if (!normalizedFilterText) {
          return true;
        }
        return String(item?.name ?? '').toLowerCase().includes(normalizedFilterText);
      })
    );
    
    // Sort items - folders first, then files, then by sort criteria
    const sortedItems = [...allItems].sort((a, b) => {
      // Folders first
      if (a.isFolder && !b.isFolder) return -1;
      if (!a.isFolder && b.isFolder) return 1;
      
      // Then sort by current sort criteria
      if (sortBy === 'name') {
        return sortOrder === 'asc' 
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name);
      } else if (sortBy === 'size') {
        const aSize = a.size || 0;
        const bSize = b.size || 0;
        return sortOrder === 'asc' 
          ? aSize - bSize
          : bSize - aSize;
      } else if (sortBy === 'modified') {
        const aDate = new Date(a.modified || 0);
        const bDate = new Date(b.modified || 0);
        return sortOrder === 'asc' 
          ? aDate - bDate
          : bDate - aDate;
      }
      return 0;
    });
    
    return sortedItems;
  }, [currentFolder, effectiveFileListFilterText, filterTreeByAccess, sortBy, sortOrder]);

  const getCurrentFilesRef = useRef(getCurrentFiles);
  getCurrentFilesRef.current = getCurrentFiles;

  const handleFileListMarqueeMouseDown = useCallback(
    (e) => {
      if (e.button !== 0 || renamingItem) return;
      if (e.target.closest('.file-list-item')) return;

      const container = fileListRef.current;
      if (!container) return;

      const coords = getFileListContentRelativeCoords(e);
      marqueeDraggingRef.current = true;
      marqueeStartRef.current = { x: coords.x, y: coords.y };
      setMarqueeRect({ left: coords.x, top: coords.y, width: 0, height: 0 });
      e.preventDefault();

      const onMove = (ev) => {
        if (!marqueeDraggingRef.current) return;
        const c = getFileListContentRelativeCoords(ev);
        const sx = marqueeStartRef.current.x;
        const sy = marqueeStartRef.current.y;
        const w = Math.abs(c.x - sx);
        const h = Math.abs(c.y - sy);
        setMarqueeRect({
          left: Math.min(sx, c.x),
          top: Math.min(sy, c.y),
          width: w,
          height: h,
        });

        const el = fileListRef.current;
        if (!el || (w < MARQUEE_DRAG_THRESHOLD_PX && h < MARQUEE_DRAG_THRESHOLD_PX)) {
          return;
        }

        const files = getCurrentFilesRef.current();
        const { unique, lastIndex } = getMarqueeSelectionForBox(el, sx, sy, c.x, c.y, files);
        setSelectedFiles(unique);
        setLastSelectedIndex(lastIndex);
      };

      const onUp = (ev) => {
        if (!marqueeDraggingRef.current) {
          return;
        }
        marqueeDraggingRef.current = false;
        marqueeListenersRef.current = { onMove: null, onUp: null };
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        document.body.style.userSelect = '';

        const el = fileListRef.current;
        if (!el) {
          setMarqueeRect(null);
          return;
        }

        const c = getFileListContentRelativeCoords(ev);
        const sx = marqueeStartRef.current.x;
        const sy = marqueeStartRef.current.y;
        const w = Math.abs(c.x - sx);
        const h = Math.abs(c.y - sy);

        if (w < MARQUEE_DRAG_THRESHOLD_PX && h < MARQUEE_DRAG_THRESHOLD_PX) {
          setMarqueeRect(null);
          setSelectedFiles([]);
          setLastSelectedIndex(-1);
          return;
        }

        const files = getCurrentFilesRef.current();
        const { unique, lastIndex } = getMarqueeSelectionForBox(el, sx, sy, c.x, c.y, files);
        setSelectedFiles(unique);
        setLastSelectedIndex(lastIndex);
        marqueeJustEndedRef.current = true;
        setMarqueeRect(null);
      };

      document.body.style.userSelect = 'none';
      marqueeListenersRef.current = { onMove, onUp };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [renamingItem, getFileListContentRelativeCoords]
  );

  const selectAll = useCallback(() => {
    const currentFiles = getCurrentFiles();
    setSelectedFiles([...currentFiles]);
  }, [getCurrentFiles]);
  
  const getSelectedFilesCount = () => selectedFiles.length;

  // Context menu handlers
  const handleRightClick = (e, item = null) => {
    e.preventDefault();
    e.stopPropagation();
    
    // If context menu is already visible, close it first and then open at new position
    // This allows right-clicking again to move the menu (Windows Explorer behavior)
    if (contextMenu.visible) {
      // Close the old menu first
      closeContextMenu();
      // Small delay to ensure state update, then open at new position
      setTimeout(() => {
        openContextMenuAtPosition(e, item);
      }, 0);
      return;
    }
    
    // Normal flow: open menu at position
    openContextMenuAtPosition(e, item);
  };

  const openContextMenuAtPosition = (e, item = null) => {
    // If right-clicking on a specific item and it's not selected, select it
    if (item && !selectedFiles.some(file => file.id === item.id)) {
      setSelectedFiles([item]);
    }
    
    // Always use the exact cursor position from the original event
    const x = e.clientX;
    const y = e.clientY;
    
    // Keep context menu within project view container bounds when available.
    const containerRect = document
      .querySelector('.project-view-container')
      ?.getBoundingClientRect();
    const bounds = containerRect
      ? {
          left: containerRect.left,
          right: containerRect.right,
          top: containerRect.top,
          bottom: containerRect.bottom
        }
      : {
          left: 0,
          right: window.innerWidth,
          top: 0,
          bottom: window.innerHeight
        };

    const menuWidth = 200;
    const menuHeight = 320; // Approximate height
    const safePadding = 6;

    const rawX = x;
    const rawY = y;

    const minX = bounds.left + safePadding;
    const maxX = bounds.right - menuWidth - safePadding;
    const minY = bounds.top + safePadding;
    const maxY = bounds.bottom - menuHeight - safePadding;

    const shouldOpenLeft = rawX + menuWidth > bounds.right - safePadding;
    const openLeftX = rawX - menuWidth;
    const preferredX = shouldOpenLeft ? openLeftX : rawX;
    const clampedX = Math.min(Math.max(preferredX, minX), Math.max(minX, maxX));
    const shouldOpenUpward = rawY + menuHeight > bounds.bottom - safePadding;
    const openUpwardY = rawY - menuHeight;
    const preferredY = shouldOpenUpward ? openUpwardY : rawY;
    const clampedY = Math.min(Math.max(preferredY, minY), Math.max(minY, maxY));

    setContextMenu({
      visible: true,
      position: { x: clampedX, y: clampedY }
    });
  };

  const closeContextMenu = () => {
    setContextMenu({ visible: false, position: { x: 0, y: 0 } });
  };

  // Context menu actions
  const handleCopy = useCallback(() => {
    if (selectedFiles.length > 0) {
      setClipboard({
        files: [...selectedFiles],
        operation: 'copy'
      });
      console.log('Copied files:', selectedFiles.map(f => f.name));
    }
  }, [selectedFiles]);

  const handleCut = useCallback(() => {
    if (selectedFiles.length > 0) {
      setClipboard({
        files: [...selectedFiles],
        operation: 'cut'
      });
      console.log('Cut files:', selectedFiles.map(f => f.name));
    }
  }, [selectedFiles]);

  const handlePaste = useCallback(async () => {
    if (!project?.Id || !currentFolder) return;
    if (clipboard.files.length === 0 || !clipboard.operation) return;

    try {
      console.log(`Pasting ${clipboard.operation} files:`, clipboard.files.map((f) => f.name));

      const destinationDriveId = (currentFolder.googleDriveId || '').trim() || null;
      const parsedFolderId = Number(currentFolder.id);
      const destinationParentId = destinationDriveId
        ? null
        : (Number.isFinite(parsedFolderId) ? parsedFolderId : null);

      const response = await fileService.pasteFiles(project.Id, {
        operation: clipboard.operation,
        items: clipboard.files,
        destinationParentId,
        destinationDriveId,
      });

      if (clipboard.operation === 'cut') {
        setClipboard({ files: [], operation: null });
      }
      setSelectedFiles([]);
      setLastSelectedIndex(-1);

      // Refresh destination folder contents after paste.
      if (destinationDriveId) {
        const driveItems = await fileService.getGoogleDriveContents(project.Id, destinationDriveId);
        const level = (currentFolder.level ?? 0) + 1;
        const formatted = driveItems.map((it) => ({
          ...it,
          level,
          children: [],
          hasChildren: it.isFolder,
          expanded: false
        }));
        const visibleChildren = filterTreeByAccess(formatted, currentFolder);

        setFileTree((prevTree) => setTreeFolderChildren(prevTree, currentFolder.id, visibleChildren));
        setCurrentFolder((prev) => {
          if (!prev || prev.id !== currentFolder.id) return prev;
          return {
            ...prev,
            children: visibleChildren,
            driveChildrenLoaded: true,
            hasChildren: visibleChildren.length > 0
          };
        });
      } else {
        await loadProjectFiles(true, { preserveFolderId: currentFolder.id });
      }

      if (Array.isArray(response?.errors) && response.errors.length > 0) {
        alert(`Paste completed with ${response.errors.length} issue(s). Check console for details.`);
        console.warn('Paste completed with issues:', response.errors);
      }
      const hasDriveClipboardItems = (clipboard.files || []).some((item) => Boolean(item?.isGoogleDriveItem));
      if (hasDriveClipboardItems) {
        signalRsaCacheSync(project?.Id);
      }
    } catch (error) {
      console.error('Error pasting files:', error);
      alert(`Error pasting files: ${error.message}`);
    }
  }, [clipboard, currentFolder, filterTreeByAccess, project?.Id, loadProjectFiles, setTreeFolderChildren]);

  const handleDelete = useCallback(() => {
    if (selectedFiles.length === 0 || deleteBlockedForSourceSubtreeFolders) {
      return;
    }
    setShowDeleteModal(true);
  }, [selectedFiles, deleteBlockedForSourceSubtreeFolders]);

  const confirmDelete = useCallback(async () => {
    const snapshot = [...selectedFiles];
    if (snapshot.length === 0) {
      setShowDeleteModal(false);
      return;
    }
    if (selectionContainsSourceSubtreeFolder(snapshot)) {
      setShowDeleteModal(false);
      return;
    }

    const dbItems = snapshot.filter((file) => !file.isGoogleDriveItem);
    const driveItems = snapshot.filter((file) => file.isGoogleDriveItem);
    const deletedIds = [
      ...dbItems.map((f) => f.id),
      ...driveItems.map((f) => f.id),
    ];

    // Block the auto-refresh poller from re-adding these Drive items while the
    // delete API call is in-flight. Cleared in both success and error paths below.
    driveItems.forEach((f) => pendingDriveDeleteIdsRef.current.add(f.id));

    const folderForDelete = currentFolder;

    // Optimistic UI: remove from tree and list immediately; delete runs in background.
    setFileTree((prevTree) => {
      const newTree = JSON.parse(JSON.stringify(prevTree));
      let updatedCurrentFolder = null;

      const removeFromTree = (items, targetId) => {
        for (let i = items.length - 1; i >= 0; i--) {
          const item = items[i];
          if (item.id === targetId) {
            items.splice(i, 1);
            return true;
          }
          if (item.children && item.children.length > 0) {
            if (removeFromTree(item.children, targetId)) {
              item.hasChildren = item.children.length > 0;
              if (folderForDelete && item.id === folderForDelete.id) {
                updatedCurrentFolder = item;
              }
              return true;
            }
          }
        }
        return false;
      };

      deletedIds.forEach((fileId) => {
        removeFromTree(newTree, fileId);
      });

      if (updatedCurrentFolder) {
        setCurrentFolder(updatedCurrentFolder);
      }

      return newTree;
    });

    setExpandedFolders((prev) => {
      const next = new Set(prev);
      deletedIds.forEach((id) => next.delete(id));
      return next;
    });

    setSelectedFiles([]);
    setLastSelectedIndex(-1);
    setShowDeleteModal(false);
    if (onClearSelection) {
      onClearSelection();
    }

    try {
      if (dbItems.length > 0) {
        const fileIds = dbItems.map((file) => file.id);
        console.log('Deleting DB files:', fileIds);
        await fileService.deleteFiles(fileIds);
      }

      if (driveItems.length > 0) {
        console.log('Deleting Google Drive files:', driveItems.map((file) => file.googleDriveId || file.id));
        await fileService.deleteGoogleDriveFiles(project?.Id, driveItems, folderForDelete?.googleDriveId || null);
        signalRsaCacheSync(project?.Id);
      }

      console.log('Files deleted successfully');
    } catch (error) {
      console.error('Error deleting files:', error);
      alert(`Error deleting files: ${error.message}`);
      await loadProjectFiles(true, { preserveFolderId: folderForDelete?.id });
    } finally {
      // Always unblock the poller, whether delete succeeded or failed.
      driveItems.forEach((f) => pendingDriveDeleteIdsRef.current.delete(f.id));
    }
  }, [
    selectedFiles,
    currentFolder,
    onClearSelection,
    project?.Id,
    fileService,
    loadProjectFiles,
    selectionContainsSourceSubtreeFolder,
  ]);

  const applyRenamedItemInUI = useCallback((targetId, newName) => {
    const updateNameInTree = (items) => {
      for (const item of items) {
        if (String(item.id) === String(targetId)) {
          item.name = newName;
          return true;
        }
        if (item.children?.length > 0) {
          if (updateNameInTree(item.children)) {
            return true;
          }
        }
      }
      return false;
    };

    const findById = (items, idToFind) => {
      for (const item of items) {
        if (String(item.id) === String(idToFind)) {
          return item;
        }
        if (item.children?.length > 0) {
          const nested = findById(item.children, idToFind);
          if (nested) return nested;
        }
      }
      return null;
    };

    setFileTree((prevTree) => {
      const newTree = JSON.parse(JSON.stringify(prevTree));
      updateNameInTree(newTree);

      if (currentFolder?.id != null) {
        const refreshedCurrentFolder = findById(newTree, currentFolder.id);
        if (refreshedCurrentFolder) {
          setCurrentFolder(refreshedCurrentFolder);
        }
      }

      return newTree;
    });

    setSelectedFiles((prev) => prev.map((file) => (
      String(file.id) === String(targetId)
        ? { ...file, name: newName }
        : file
    )));

    if (onBreadcrumbUpdate && Array.isArray(currentPath) && currentPath.length > 0) {
      const nextPath = currentPath.map((segment) => (
        String(segment?.id) === String(targetId)
          ? { ...segment, name: newName }
          : segment
      ));
      onBreadcrumbUpdate(nextPath);
    }
  }, [currentFolder?.id, currentPath, onBreadcrumbUpdate]);

  const cancelInlineRename = useCallback(() => {
    setRenamingItem(null);
    setRenameDraft('');
    skipRenameBlurCommitRef.current = false;
  }, []);

  const commitInlineRename = useCallback(async () => {
    if (!project?.Id || !renamingItem) {
      return;
    }

    const itemSnapshot = renamingItem;
    const targetId = itemSnapshot.id;
    const previousName = itemSnapshot.name;
    // Extension is hard-locked: re-attach the original extension if the user
    // removed or changed it, so videos/files never lose their loadable suffix.
    const newName = enforceExtension(renameDraft, previousName);
    if (!newName || newName === previousName) {
      cancelInlineRename();
      return;
    }

    // Refuse to commit a colliding name. Input stays open with the banner.
    if (!renameValidation.ok) {
      return;
    }

    // Optimistic UX: leave edit mode and update label immediately.
    cancelInlineRename();
    applyRenamedItemInUI(targetId, newName);
    // Registered BEFORE the await: a poll that fires during the request must
    // already see the guard, or it will fetch the old name and repaint it.
    if (itemSnapshot.isGoogleDriveItem) {
      pendingDriveRenamesRef.current.set(String(targetId), newName);
    }

    try {
      await fileService.renameFile(project.Id, itemSnapshot, newName);
    } catch (error) {
      // Two different questions. Releasing the guard asks "is the pending entry
      // still the one this call registered?" — a newer rename may have superseded
      // it. Rolling the label back asks "has anything newer superseded us?", which
      // for a non-Drive item is vacuously true because no guard was ever set.
      // Sharing one condition silently skipped the rollback for non-Drive renames.
      const stillOurs =
        !itemSnapshot.isGoogleDriveItem ||
        pendingDriveRenamesRef.current.get(String(targetId)) === newName;
      if (itemSnapshot.isGoogleDriveItem && stillOurs) {
        pendingDriveRenamesRef.current.delete(String(targetId));
      }
      if (stillOurs) {
        applyRenamedItemInUI(targetId, previousName);
      }
      console.error('Error renaming item:', error);
      alert(`Error renaming item: ${error.message}`);
    }
  }, [project?.Id, renamingItem, renameDraft, applyRenamedItemInUI, cancelInlineRename, renameValidation]);

  const startInlineRename = useCallback((item) => {
    if (!item) {
      return;
    }
    skipRenameBlurCommitRef.current = false;
    setRenamingItem(item);
    setRenameDraft(item.name || '');
  }, []);

  const newFolderValidation = useMemo(() => {
    if (!creatingFolder) return { ok: true, suggested: null };
    const draft = String(newFolderDraft || '').trim();
    if (!draft) return { ok: true, suggested: null };
    const peerNames = (currentFolder?.children || []).map((p) => p.name);
    const collides = peerNames.some(
      (n) => normalizeNameForCompare(n) === normalizeNameForCompare(draft)
    );
    if (!collides) return { ok: true, suggested: null };
    return { ok: false, suggested: pickAvailableName(draft, new Set(peerNames)) };
  }, [creatingFolder, newFolderDraft, currentFolder?.children]);

  const cancelNewFolder = useCallback(() => {
    setCreatingFolder(false);
    setNewFolderDraft('');
  }, []);

  // Add a folder to the current folder without refreshing
  // Declared here (rather than next to addFileToCurrentFolder below) because
  // commitNewFolder's dep array references it, and `const` declarations are
  // in the temporal dead zone until evaluated — keeping it below would
  // throw "Cannot access 'addFolderToCurrentFolder' before initialization"
  // on every render.
  const addFolderToCurrentFolder = useCallback((folderData) => {
    const isDriveFolder =
      !!folderData?.isGoogleDriveItem
      || (typeof folderData?.id === 'string' && folderData.id.startsWith('gd_'));
    const formattedFolder = isDriveFolder
      ? {
          children: [],
          hasChildren: false,
          expanded: false,
          level: 0,
          ...folderData,
        }
      : fileService.formatFileForFrontend(folderData);

    // Update file tree
    setFileTree(prevTree => {
      const newTree = JSON.parse(JSON.stringify(prevTree)); // Deep clone
      let updatedCurrentFolder = null;

      const addFolderToTree = (items, targetParentId) => {
        for (const item of items) {
          const matchesDriveParent =
            formattedFolder.parentDriveId
            && item.googleDriveId
            && String(item.googleDriveId) === String(formattedFolder.parentDriveId);
          if (item.id === targetParentId || matchesDriveParent) {
            // Found the parent folder, add folder to its children
            if (!item.children) {
              item.children = [];
            }
            item.children.push({ ...formattedFolder, children: [], hasChildren: false, level: item.level + 1 });
            item.hasChildren = true;
            // Sort children (folders first, then files, alphabetically)
            item.children.sort((a, b) => {
              if (a.isFolder && !b.isFolder) return -1;
              if (!a.isFolder && b.isFolder) return 1;
              return a.name.localeCompare(b.name);
            });
            // If this is the current folder, save reference for update
            if (currentFolder && item.id === currentFolder.id) {
              updatedCurrentFolder = item;
            }
            return true;
          }
          if (item.children && item.children.length > 0) {
            if (addFolderToTree(item.children, targetParentId)) {
              return true;
            }
          }
        }
        return false;
      };

      // If parent is root (project.Id), add to root folder's children
      if (formattedFolder.parentId === project.Id || formattedFolder.parentId === null) {
        // Find the root folder (usually first item in tree, or a folder with parent_id === project.Id)
        const rootFolder = newTree.find(item =>
          (item.isFolder && (item.parentId === project.Id || item.parentId === null || item.id === project.Id)) ||
          (item.id === project.Id)
        ) || newTree[0]; // Fallback to first item

        if (rootFolder) {
          if (!rootFolder.children) {
            rootFolder.children = [];
          }
          rootFolder.children.push({ ...formattedFolder, children: [], hasChildren: false, level: (rootFolder.level || 0) + 1 });
          rootFolder.hasChildren = true;
          // Sort children
          rootFolder.children.sort((a, b) => {
            if (a.isFolder && !b.isFolder) return -1;
            if (!a.isFolder && b.isFolder) return 1;
            return a.name.localeCompare(b.name);
          });
          // If viewing root, update current folder
          if (currentFolder && (currentFolder.id === rootFolder.id || currentFolder.id === 'root' || currentFolder.id === project.Id)) {
            updatedCurrentFolder = rootFolder;
          }
        } else {
          // No root folder found, add as root level item
          newTree.push({ ...formattedFolder, children: [], hasChildren: false, level: 0 });
          newTree.sort((a, b) => {
            if (a.isFolder && !b.isFolder) return -1;
            if (!a.isFolder && b.isFolder) return 1;
            return a.name.localeCompare(b.name);
          });
        }
      } else {
        addFolderToTree(newTree, formattedFolder.parentId);
      }

      // Update current folder reference if it was modified
      if (updatedCurrentFolder) {
        setCurrentFolder(updatedCurrentFolder);
      }

      return newTree;
    });

    console.log('✅ Folder added to current folder view');
  }, [project, currentFolder, fileService]);

  const commitNewFolder = useCallback(async () => {
    if (!project?.Id || !creatingFolder) return;
    const name = String(newFolderDraft || '').trim();
    if (!name) {
      cancelNewFolder();
      return;
    }
    if (!newFolderValidation.ok) return;  // banner is up; do nothing on submit

    cancelNewFolder();  // optimistic close

    try {
      const parentId = currentFolder?.id || project.Id;
      const parentGoogleDriveId = currentFolder?.googleDriveId || null;
      const response = await fileService.createFolder(
        project.Id, name, parentId, parentGoogleDriveId
      );
      const folderForExplorer = response?.drive_item || response?.folder;
      if (folderForExplorer) {
        addFolderToCurrentFolder(folderForExplorer);
      } else {
        loadProjectFiles(true, { preserveFolderId: currentFolder?.id });
      }
    } catch (error) {
      alert(`Error creating folder: ${error.message}`);
      console.error('Error creating folder:', error);
    }
  }, [
    project?.Id,
    creatingFolder,
    newFolderDraft,
    newFolderValidation,
    currentFolder?.id,
    currentFolder?.googleDriveId,
    cancelNewFolder,
    loadProjectFiles,
    addFolderToCurrentFolder,
  ]);

  const startInlineNewFolder = useCallback(() => {
    setCreatingFolder(true);
    setNewFolderDraft('New folder');
    // Select-all the placeholder text on next tick so the user can type-replace.
    setTimeout(() => {
      if (newFolderInputRef.current) {
        newFolderInputRef.current.focus();
        newFolderInputRef.current.select?.();
      }
    }, 0);
  }, []);

  const handleRename = useCallback(() => {
    if (selectedFiles.length !== 1) {
      return;
    }
    startInlineRename(selectedFiles[0]);
  }, [selectedFiles, startInlineRename]);

  const closeDownloadNotification = () => {
    // Trigger closing animation
    setIsClosingNotification(true);
    // Hide after animation completes (0.6s)
    setTimeout(() => {
      setShowDownloadNotification(false);
      setIsClosingNotification(false);
      setDownloadProgress(0);
      setDownloadStatus('');
      setIsMultipleFiles(false);
    }, 600);
  };

  const handleCancelDownload = () => {
    // Cancel the axios request silently
    if (downloadCancelToken) {
      downloadCancelToken.cancel('Download cancelled by user');
      setDownloadCancelToken(null);
    }
    
    // Clear progress interval if it exists
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
    
    // Close with animation
    closeDownloadNotification();
    
    console.log('✅ Download cancelled by user');
  };

  const handleDownload = async (filesArg) => {
    // Per-row buttons pass an explicit [item]; the context menu / toolbar call
    // with no arg (or an event) and fall back to the current selection.
    const files = Array.isArray(filesArg) ? filesArg : selectedFiles;
    if (files.length > 0) {
      // Folders are downloadable: each zip route expands a folder with its subtree,
      // so the archive mirrors what the explorer shows.
      const dbFilesToDownload = files.filter((file) => !file.isGoogleDriveItem);
      const driveFilesToDownload = files.filter(
        (file) => file.isGoogleDriveItem && !!file.googleDriveId
      );
      const downloadableCount = dbFilesToDownload.length + driveFilesToDownload.length;

      console.log('Downloading files:', files.map(f => f.name));

      if (downloadableCount === 0) {
        alert('Please select at least one file to download.');
        return;
      }

      // Project-prefixed names for Deliverables downloads ('' outside Deliverables).
      const downloadOptions = { namePrefix: resolveDownloadNamePrefix(files) };

      if (dbFilesToDownload.length > 0) {
        // A folder always goes through the zip, even on its own.
        const isMultiple = dbFilesToDownload.length > 1 || !!dbFilesToDownload[0].isFolder;

        // Create cancel token for axios (DB file download flow)
        const CancelToken = axios.CancelToken;
        const source = CancelToken.source();
        setDownloadCancelToken(source);

        if (isMultiple) {
          // ZIP downloads - don't show old popup, use Downloads popup instead
          try {
            await fileService.downloadFiles(dbFilesToDownload, null, source, downloadOptions);
            setDownloadCancelToken(null);
          } catch (error) {
            setDownloadCancelToken(null);
            // Handle cancellation silently - no alerts, just cleanup
            if (axios.isCancel(error)) {
              console.log('Download cancelled by user');
              return;
            }

            // Only show alert for actual errors (not cancellations)
            console.error('Error downloading files:', error);
            alert(`Error downloading files: ${error.message}`);
            setDownloadCancelToken(null);
            return;
          }
        } else {
          // Single file download - show download notification
          setIsClosingNotification(false);
          setShowDownloadNotification(true);
          setIsMultipleFiles(false);
          setDownloadProgress(0);
          setDownloadStatus('downloading');

          try {
            // Don't pass progress callback for single file downloads
            await fileService.downloadFiles(dbFilesToDownload, null, source, downloadOptions);

            setDownloadCancelToken(null);

            // Keep popup visible after DB single-file download only when no Drive downloads follow.
            if (driveFilesToDownload.length === 0) {
              setTimeout(() => {
                closeDownloadNotification();
              }, 2500);
            }
          } catch (error) {
            // Handle cancellation silently - no alerts, just cleanup
            if (axios.isCancel(error)) {
              console.log('Download cancelled by user');
              // Silently close popup - already handled by handleCancelDownload
              return; // Exit early, don't reset state again (already done in handleCancelDownload)
            }

            // Only show alert for actual errors (not cancellations)
            console.error('Error downloading files:', error);
            alert(`Error downloading files: ${error.message}`);
            closeDownloadNotification();
            setDownloadCancelToken(null);
            return;
          }
        }
      }

      if (driveFilesToDownload.length > 0) {
        try {
          const driveHasFolder = driveFilesToDownload.some((file) => file.isFolder);
          if (driveFilesToDownload.length > 3 || driveHasFolder) {
            // For larger Drive selections, download one ZIP and track via global Downloads popup.
            setShowDownloadNotification(false);
            setIsClosingNotification(false);
            setDownloadProgress(0);
            setDownloadStatus('');
            setIsMultipleFiles(false);
            const driveCancelToken = axios.CancelToken.source();
            setDownloadCancelToken(driveCancelToken);
            const singleDriveFolder = driveFilesToDownload.length === 1 && driveHasFolder
              ? driveFilesToDownload[0]
              : null;
            await fileService.downloadGoogleDriveFilesAsZip(
              driveFilesToDownload,
              singleDriveFolder
                ? `${String(singleDriveFolder.name || 'folder').replace(/[\\/:*?"<>|]+/g, '-')}.zip`
                : `drive_files_${driveFilesToDownload.length}_items.zip`,
              null,
              driveCancelToken,
              false,
              downloadOptions
            );
            setDownloadCancelToken(null);
          } else {
            setIsClosingNotification(false);
            setShowDownloadNotification(true);
            setIsMultipleFiles(true);
            setDownloadProgress(0);
            setDownloadStatus('downloading');

            await fileService.downloadGoogleDriveFiles(driveFilesToDownload, (progress, status) => {
              setDownloadProgress(progress ?? 0);
              setDownloadStatus(status || 'downloading');
            }, downloadOptions);

            setTimeout(() => {
              closeDownloadNotification();
            }, 2500);
          }
        } catch (error) {
          console.error('Error downloading Google Drive files:', error);
          alert(`Error downloading Google Drive files: ${error.message}`);
          closeDownloadNotification();
        }
      }
    }
  };

  const handleRefresh = useCallback(() => {
    loadProjectFiles(true, { preserveFolderId: currentFolder?.id });
    setSelectedFiles([]);
  }, [loadProjectFiles, currentFolder?.id]);

  // Add a file to the current folder without refreshing
  const addFileToCurrentFolder = useCallback((fileData) => {
    const formattedFile = fileService.formatFileForFrontend(fileData);
    
    // Update file tree
    setFileTree(prevTree => {
      const newTree = JSON.parse(JSON.stringify(prevTree)); // Deep clone
      let updatedCurrentFolder = null;
      
      const addFileToTree = (items, targetParentId) => {
        for (const item of items) {
          if (item.id === targetParentId) {
            // Found the parent folder, add file to its children
            if (!item.children) {
              item.children = [];
            }
            item.children.push({ ...formattedFile, children: [], hasChildren: false, level: item.level + 1 });
            item.hasChildren = true;
            // Sort children (folders first, then files, alphabetically)
            item.children.sort((a, b) => {
              if (a.isFolder && !b.isFolder) return -1;
              if (!a.isFolder && b.isFolder) return 1;
              return a.name.localeCompare(b.name);
            });
            // If this is the current folder, save reference for update
            if (currentFolder && item.id === currentFolder.id) {
              updatedCurrentFolder = item;
            }
            return true;
          }
          if (item.children && item.children.length > 0) {
            if (addFileToTree(item.children, targetParentId)) {
              return true;
            }
          }
        }
        return false;
      };
      
      // If parent is root (project.Id), add to root folder's children
      if (formattedFile.parentId === project.Id || formattedFile.parentId === null) {
        // Find the root folder (usually first item in tree, or a folder with parent_id === project.Id)
        const rootFolder = newTree.find(item => 
          (item.isFolder && (item.parentId === project.Id || item.parentId === null || item.id === project.Id)) ||
          (item.id === project.Id)
        ) || newTree[0]; // Fallback to first item
        
        if (rootFolder) {
          if (!rootFolder.children) {
            rootFolder.children = [];
          }
          rootFolder.children.push({ ...formattedFile, children: [], hasChildren: false, level: (rootFolder.level || 0) + 1 });
          rootFolder.hasChildren = true;
          // Sort children
          rootFolder.children.sort((a, b) => {
            if (a.isFolder && !b.isFolder) return -1;
            if (!a.isFolder && b.isFolder) return 1;
            return a.name.localeCompare(b.name);
          });
          // If viewing root, update current folder
          if (currentFolder && (currentFolder.id === rootFolder.id || currentFolder.id === 'root' || currentFolder.id === project.Id)) {
            updatedCurrentFolder = rootFolder;
          }
        } else {
          // No root folder found, add as root level item
          newTree.push({ ...formattedFile, children: [], hasChildren: false, level: 0 });
          newTree.sort((a, b) => {
            if (a.isFolder && !b.isFolder) return -1;
            if (!a.isFolder && b.isFolder) return 1;
            return a.name.localeCompare(b.name);
          });
        }
      } else {
        addFileToTree(newTree, formattedFile.parentId);
      }
      
      // Update current folder reference if it was modified
      if (updatedCurrentFolder) {
        setCurrentFolder(updatedCurrentFolder);
      }
      
      return newTree;
    });
    
    console.log('✅ File added to current folder view');
  }, [project, currentFolder, fileService]);

  // Expose handleDownload, handleRefresh, addFileToCurrentFolder, addFolderToCurrentFolder, and selectedFiles to parent component via ref
  useImperativeHandle(ref, () => ({
    // Mobile: the Deliverables breadcrumb pill hosts the search icon (it
    // lives in RSAAnalysis.js, outside this component), so opening the
    // search bar is driven from there.
    toggleMobileSearch: () => {
      setMobileSearchOpen((v) => {
        if (v) handleFileListFilterChange('');
        return !v;
      });
    },
    handleDownload: () => {
      if (selectedFiles.length > 0) {
        handleDownload();
      } else {
        alert('Please select files to download');
      }
    },
    // Download every file in the current folder (no selection needed) —
    // multiple files zip via the existing downloadFilesAsZip path.
    handleDownloadAll: () => {
      const all = getCurrentFiles().filter((f) => !f.isFolder);
      if (all.length === 0) {
        alert('No files to download in this folder');
        return;
      }
      handleDownload(all);
    },
    handleDelete: () => {
      handleDelete();
    },
    handleRefresh: () => {
      handleRefresh();
    },
    addFileToCurrentFolder: (fileData) => {
      addFileToCurrentFolder(fileData);
    },
    addFolderToCurrentFolder: (folderData) => {
      addFolderToCurrentFolder(folderData);
    },
    startInlineNewFolder: () => {
      startInlineNewFolder();
    },
    renameRootFolder,
    getCurrentFolderContext: () => (
      currentFolder
        ? {
            id: currentFolder.id,
            name: currentFolder.name,
            googleDriveId: currentFolder.googleDriveId || null,
            isFolder: !!currentFolder.isFolder,
            isGoogleDriveItem: !!currentFolder.isGoogleDriveItem,
            parentId: currentFolder.parentId ?? null,
            parentDriveId: currentFolder.parentDriveId || null,
          }
        : null
    ),
    hasSelectedFiles: selectedFiles.length > 0,
    canDeleteSelection:
      selectedFiles.length > 0 && !deleteBlockedForSourceSubtreeFolders,
  }), [
    currentFolder,
    selectedFiles,
    deleteBlockedForSourceSubtreeFolders,
    handleDownload,
    handleDelete,
    handleRefresh,
    addFileToCurrentFolder,
    addFolderToCurrentFolder,
    startInlineNewFolder,
    renameRootFolder,
  ]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (contextMenu.visible) return; // Don't handle shortcuts when context menu is open
      if (renamingItem) return; // Let inline rename input handle keyboard

      const target = e.target;
      if (target instanceof HTMLElement) {
        const isEditableTarget =
          target.isContentEditable ||
          ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) ||
          Boolean(target.closest('[contenteditable="true"]'));

        if (isEditableTarget) {
          return;
        }
      }
      
      if (e.ctrlKey || e.metaKey) {
        switch (e.key.toLowerCase()) {
          case 'c':
            e.preventDefault();
            handleCopy();
            break;
          case 'x':
            e.preventDefault();
            handleCut();
            break;
          case 'v':
            e.preventDefault();
            handlePaste();
            break;
          case 'a':
            e.preventDefault();
            selectAll();
            break;
          default:
            // No action for other key combinations
            break;
        }
      } else {
        switch (e.key) {
          case 'Escape':
            e.preventDefault();
            clearSelection();
            break;
          case 'Delete':
            e.preventDefault();
            handleDelete();
            break;
          case 'F2':
            e.preventDefault();
            handleRename();
            break;
          case 'F5':
            e.preventDefault();
            handleRefresh();
            break;
          default:
            // No action for other keys
            break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    selectedFiles,
    contextMenu.visible,
    renamingItem,
    clipboard,
    clearSelection,
    handleCopy,
    handleCut,
    handleDelete,
    handlePaste,
    handleRefresh,
    handleRename,
    selectAll,
    deleteBlockedForSourceSubtreeFolders,
  ]);


  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      // Dates read most-recent-first; text reads A-Z.
      setSortOrder(column === 'modified' ? 'desc' : 'asc');
    }
  };


  const renderTreeItem = (item, level = 0) => {
    // Only render folders in the tree view
    if (!item.isFolder) {
      return null;
    }
    
    const isExpanded = expandedFolders.has(item.id);
    const isSelected = currentFolder && currentFolder.id === item.id;
    const hasChildren =
      (item.children && item.children.filter((child) => child.isFolder).length > 0) ||
      (item.isFolder && item.googleDriveId) ||
      (item.isFolder && item.s3Key);
    const isLoadingDrive = driveLoadingFolderId === item.id;
    
    // Open folder icon when expanded; closed folder when collapsed
    let icon;
    if (item.isFolder) {
      icon = isExpanded ? 'fa-solid fa-folder-open' : 'fa-solid fa-folder';
    } else {
      icon = fileService.getFileIcon(item);
    }
    
    return (
      <div key={item.id} className="file-tree-item-wrapper">
        <div 
          className={`file-tree-item ${isSelected ? 'selected' : ''}`}
          style={{ paddingLeft: `${level * 12 + 2}px` }}
          onClick={(e) => {
            e.stopPropagation();
            handleTreeFolderClick(item);
          }}
        >
          <div className="file-tree-item-content">
            <button 
              className="expand-btn"
              onClick={async (e) => {
                e.stopPropagation();
                if (item.googleDriveId && !item.driveChildrenLoaded) {
                  await handleExpandWithDrive(item);
                } else {
                  toggleFolder(item.id);
                }
              }}
              disabled={isLoadingDrive}
              title={isLoadingDrive ? 'Loading...' : null}
            >
              {isLoadingDrive ? (
                <i className="fas fa-spinner fa-spin"></i>
              ) : (
                <i className={`fas fa-chevron-right ${isExpanded ? 'expanded' : ''}`}></i>
              )}
            </button>
            
            <div className="file-icon">
              {renderFileIcon(item, icon)}
            </div>
            
            <span className="file-name">{item.name}</span>
          </div>
        </div>
        
        {isExpanded && hasChildren && (
          <div className="tree-children">
            {item.children
              .filter(child => child.isFolder)
              .map(child => renderTreeItem(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const renderFileListHeader = () => (
    <div className="file-list-header">
      <div 
        className="header-cell icon" 
        style={{ width: `${columnWidths.icon}px` }}
      >
        <i className="fas fa-image"></i>
      </div>
      
      <div
        className={`header-cell name ${sortBy === 'name' ? 'active' : ''}`}
        style={{ flex: 1 }}
        onClick={() => handleSort('name')}
      >
        <span>Name</span>
        {sortBy === 'name' && (
          <i className={`fas fa-arrow-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>
        )}
      </div>
      <div
        className={`header-cell size ${sortBy === 'size' ? 'active' : ''}`}
        style={{ width: `${columnWidths.size}px` }}
        onClick={() => handleSort('size')}
      >
        <span>Size</span>
        {sortBy === 'size' && (
          <i className={`fas fa-arrow-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>
        )}
      </div>

      <div
        className={`header-cell modified ${sortBy === 'modified' ? 'active' : ''}`}
        style={{ width: `${columnWidths.modified}px` }}
        onClick={() => handleSort('modified')}
      >
        <span>Modified</span>
        {sortBy === 'modified' && (
          <i className={`fas fa-arrow-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>
        )}
      </div>

      <div
        className={`header-cell type ${sortBy === 'type' ? 'active' : ''}`}
        style={{ width: `${columnWidths.type}px` }}
        onClick={() => handleSort('type')}
      >
        <span>Type</span>
        {sortBy === 'type' && (
          <i className={`fas fa-arrow-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>
        )}
      </div>

      <div className="header-cell hover-actions-spacer" aria-hidden="true"></div>
      <div className="header-cell action" aria-hidden="true"></div>
    </div>
  );

  const handlePlayVideo = (e, file) => {
    e.stopPropagation(); // Prevent file selection when clicking play icon
    setSelectedVideoFile(file);
    setShowVideoModal(true);
  };

  const handleViewImage = (e, file) => {
    e.stopPropagation(); // Prevent file selection when clicking view icon
    setSelectedImageFile(file);
    setShowImageModal(true);
  };

  // Color-coded type tile: maps a file to its tile color class. The tinted
  // square behind each icon lets the list be scanned by data type at a glance
  // (point clouds, routes, results, reports, media). Drives `.file-tile.tile-*`
  // styling in FileExplorer.css. The icon glyph itself still comes from
  // renderFileIcon so branded CSV/XML/DOC/PDF icons are preserved.
  const getFileTileClass = (item) => {
    if (item?.isFolder) return 'tile-folder';
    const ext = String(item?.name || '').split('.').pop()?.toLowerCase() || '';
    switch (ext) {
      case 'xml':
      case 'json':
      case 'kml':
      case 'js':
      case 'jsx':
      case 'ts':
      case 'tsx':
      case 'html':
      case 'css':
      case 'py':
        return 'tile-code';
      case 'csv':
      case 'xls':
      case 'xlsx':
        return 'tile-data';
      case 'las':
      case 'laz':
      case 'ply':
      case 'pts':
      case 'pcd':
      case 'e57':
        return 'tile-cloud';
      case 'mp4':
      case 'mov':
      case 'avi':
      case 'mkv':
      case 'webm':
        return 'tile-video';
      case 'doc':
      case 'docx':
      case 'txt':
      case 'rtf':
        return 'tile-doc';
      case 'pdf':
        return 'tile-pdf';
      case 'ppt':
      case 'pptx':
        return 'tile-slide';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'bmp':
      case 'webp':
      case 'svg':
      case 'tif':
      case 'tiff':
      case 'ico':
        return 'tile-image';
      case 'zip':
      case 'rar':
      case '7z':
      case 'gz':
      case 'tar':
        return 'tile-archive';
      case 'mp3':
      case 'wav':
      case 'flac':
        return 'tile-audio';
      default:
        return 'tile-default';
    }
  };

  const renderFileListItem = (item, index) => {
    const isSelected = selectedFiles.some(file => file.id === item.id);
    const isRenamingThisItem = !!renamingItem && String(renamingItem.id) === String(item.id);
    const icon = fileService.getFileIcon(item);
    const fileName = String(item.name || '');
    const lastDotIndex = fileName.lastIndexOf('.');
    const hasValidExtension = !item.isFolder && lastDotIndex > 0 && lastDotIndex < fileName.length - 1;
    const fileExtension = item.isFolder
      ? 'Folder'
      : (hasValidExtension ? fileName.slice(lastDotIndex + 1).toLowerCase() : 'N/A');
    const isVideoFile = !item.isFolder && hasValidExtension && fileExtension === 'mp4';
    // Common image extensions
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'ico', 'tiff', 'tif'];
    const isImageFile = !item.isFolder && hasValidExtension && imageExtensions.includes(fileExtension);
    const tileClass = getFileTileClass(item);

    return (
      <div 
        key={item.id}
        className={`file-list-item ${index % 2 === 0 ? 'file-list-item-even' : 'file-list-item-odd'} ${isSelected ? 'selected' : ''} ${item.isFolder ? 'folder-item' : 'is-file'}`}
        onClick={(e) => {
          // Mobile: long-press selects (see touch handlers); while a selection
          // exists, plain taps toggle rows; with none, a tap opens the item.
          if (isMobile) {
            e.preventDefault();
            e.stopPropagation();
            // A long-press already handled this gesture — swallow the
            // synthetic click that follows touchend.
            if (longPressFiredRef.current) {
              longPressFiredRef.current = false;
              return;
            }
            if (renamingItem) return;
            if (selectedFiles.length > 0) {
              setSelectedFiles((prev) =>
                prev.some((f) => f.id === item.id)
                  ? prev.filter((f) => f.id !== item.id)
                  : [...prev, item]
              );
              return;
            }
            handleFileListDoubleClick(item);
            return;
          }
          handleFileListClick(item, index, e);
        }}
        onTouchStart={(e) => {
          if (!isMobile || renamingItem) return;
          // A NEW gesture invalidates any pending click-swallow: Android
          // often suppresses the synthetic click after a long-press, and a
          // flag left set would eat the next legitimate tap instead (the
          // "first tap after long-press does nothing" bug).
          longPressFiredRef.current = false;
          const t = e.touches[0];
          touchStartPosRef.current = { x: t.clientX, y: t.clientY };
          clearTimeout(longPressTimerRef.current);
          longPressTimerRef.current = setTimeout(() => {
            longPressFiredRef.current = true;
            // Subtle confirm, like the native file managers. Optional API.
            try { navigator.vibrate?.(15); } catch { /* not supported */ }
            setSelectedFiles((prev) =>
              prev.some((f) => f.id === item.id)
                ? prev.filter((f) => f.id !== item.id)
                : [...prev, item]
            );
          }, 450);
        }}
        onTouchMove={(e) => {
          // A drag is a scroll, not a long-press — >10px cancels the timer.
          const t = e.touches[0];
          const dx = t.clientX - touchStartPosRef.current.x;
          const dy = t.clientY - touchStartPosRef.current.y;
          if (dx * dx + dy * dy > 100) clearTimeout(longPressTimerRef.current);
        }}
        onTouchEnd={() => clearTimeout(longPressTimerRef.current)}
        onTouchCancel={() => clearTimeout(longPressTimerRef.current)}
        onDoubleClick={() => {
          // Mobile: two quick taps synthesise dblclick — in selection mode
          // that opened the item mid-toggle. Single tap already owns open
          // (no selection) and toggle (selection), so dblclick has no
          // mobile job at all.
          if (isMobile || renamingItem) return;
          handleFileListDoubleClick(item);
        }}
        onContextMenu={(e) => {
          // Ensure file item right-click handler fires even when backdrop is visible
          // Stop propagation early so backdrop doesn't capture it
          e.stopPropagation();
          // Mobile: Android synthesises contextmenu from the same long-press
          // that drives selection — suppress it or the browser callout races
          // the selection toggle.
          if (isMobile) { e.preventDefault(); return; }
          handleRightClick(e, item);
        }}
      >
        <div
          className="file-icon"
          style={{ width: `${columnWidths.icon}px` }}
        >
          <span className={`file-tile ${tileClass}`} aria-hidden="true">
            {renderFileIcon(item, icon)}
          </span>
        </div>
        <div
          className="file-name"
          style={{ flex: 1 }}
        >
          {isRenamingThisItem ? (
            <div className="rename-input-wrapper">
              <input
                ref={renameInputRef}
                type="text"
                className={`file-name-text file-name-input ${!renameValidation.ok ? 'has-conflict' : ''}`}
                value={renameDraft}
                style={{
                  width: `${Math.max(12, (renameDraft || '').length + 2)}ch`,
                  minWidth: '100px',
                  maxWidth: '100%',
                }}
                onChange={(e) => setRenameDraft(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onDoubleClick={(e) => e.stopPropagation()}
                onContextMenu={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.code === 'Enter' || e.code === 'NumpadEnter') {
                    e.preventDefault();
                    e.stopPropagation();
                    // Match Windows-like behavior: Enter confirms rename via blur-save path.
                    skipRenameBlurCommitRef.current = false;
                    e.currentTarget.blur();
                    return;
                  }
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    e.stopPropagation();
                    skipRenameBlurCommitRef.current = true;
                    cancelInlineRename();
                  }
                }}
                onBlur={() => {
                  if (skipRenameBlurCommitRef.current) {
                    skipRenameBlurCommitRef.current = false;
                    return;
                  }
                  void commitInlineRename();
                }}
              />
              {!renameValidation.ok && (
                <div className="rename-conflict-banner">
                  <span>A {renamingItem?.isFolder ? 'folder' : 'file'} named "{enforceExtension(renameDraft, renamingItem?.name)}" already exists.</span>
                  <button
                    type="button"
                    className="rename-conflict-suggest"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => setRenameDraft(renameValidation.suggested)}
                  >
                    Use "{renameValidation.suggested}" instead
                  </button>
                </div>
              )}
            </div>
          ) : (
            <span className="file-name-text">{item.name}</span>
          )}
          {!isRenamingThisItem && isVideoFile && (
            <button
              className="video-play-button"
              onClick={(e) => handlePlayVideo(e, item)}
              title="Play video"
              aria-label="Play video"
            >
              <span className="video-play-icon-circle">
                <i className="fas fa-play"></i>
              </span>
              <span className="video-play-text">Play</span>
            </button>
          )}
          {!isRenamingThisItem && isImageFile && (
            <button
              className="image-view-button"
              onClick={(e) => handleViewImage(e, item)}
              title="View image"
              aria-label="View image"
            >
              <span className="image-view-icon-circle">
                <i className="fas fa-image"></i>
              </span>
              <span className="image-view-text">View</span>
            </button>
          )}
        </div>
        <div 
          className="file-size"
          style={{ width: `${columnWidths.size}px` }}
        >
          {item.isFolder ? '--' : fileService.formatFileSize(item.size)}
        </div>
        <div
          className="file-modified"
          style={{ width: `${columnWidths.modified}px` }}
          title={item.modified ? new Date(item.modified).toLocaleString() : undefined}
        >
          {item.modified ? formatModified(item.modified) : '--'}
        </div>
        <div
          className="file-type"
          style={{ width: `${columnWidths.type}px` }}
        >
          <span className="file-type-badge">
            {item.isFolder ? 'Folder' : fileExtension.toUpperCase()}
          </span>
        </div>
        {/* Google-Drive-style quick actions, revealed on row hover.
            Mobile selection mode (entered by long-press): every row's action
            slot becomes a round checkbox instead — Download would be one
            mis-tap away from firing while picking rows. The circle is
            passive (pointer-events:none): the row's own tap toggles, so
            there is no double-toggle race. */}
        <div className="file-row-hover-actions">
          {isMobile && selectedFiles.length > 0 ? (
            <span
              className={`row-select-circle${isSelected ? ' checked' : ''}`}
              aria-hidden="true"
            >
              {isSelected && <i className="fas fa-check"></i>}
            </span>
          ) : (
          <>
          <button
            type="button"
            className="row-action-btn"
            title={item.isFolder ? 'Download folder as zip' : 'Download'}
            aria-label={item.isFolder ? 'Download folder as zip' : 'Download'}
            onClick={(e) => {
              e.stopPropagation();
              handleDownload([item]);
            }}
          >
            <i className="fas fa-download" aria-hidden="true"></i>
          </button>
          {!isMobile && (
          <button
            type="button"
            className="row-action-btn"
            title="Rename"
            aria-label="Rename"
            onClick={(e) => {
              e.stopPropagation();
              // Select the row first so the inline input matches the F2 rename
              // styling (.file-list-item.selected .file-name-input).
              setSelectedFiles([item]);
              startInlineRename(item);
            }}
          >
            <i className="fas fa-pen" aria-hidden="true"></i>
          </button>
          )}
          {!isMobile && (
          <button
            type="button"
            className="row-action-btn"
            title="Delete"
            aria-label="Delete"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedFiles([item]);
              setShowDeleteModal(true);
            }}
          >
            <i className="fas fa-trash" aria-hidden="true"></i>
          </button>
          )}
          </>
          )}
        </div>
        {!isMobile && (
        <div className="file-row-action">
          <button
            type="button"
            className="row-action-btn"
            title="More actions"
            aria-label="More actions"
            onClick={(e) => {
              e.stopPropagation();
              handleRightClick(e, item);
            }}
          >
            <i className="fas fa-ellipsis-vertical" aria-hidden="true"></i>
          </button>
        </div>
        )}
      </div>
    );
  };

  const renderFileIcon = (item, iconClass) => {
    const fileName = String(item?.name || '').toLowerCase();
    const isCsvFile = !item?.isFolder && fileName.endsWith('.csv');
    const isXmlFile = !item?.isFolder && fileName.endsWith('.xml');
    const isWordFile = !item?.isFolder && (fileName.endsWith('.doc') || fileName.endsWith('.docx'));
    const isPdfFile = !item?.isFolder && fileName.endsWith('.pdf');

    if (isCsvFile) {
      return <img src={csvIcon} alt="" className="file-icon-image file-icon-image-csv" aria-hidden="true" />;
    }

    if (isXmlFile) {
      return <img src={xmlIcon} alt="" className="file-icon-image file-icon-image-xml" aria-hidden="true" />;
    }

    if (isWordFile) {
      return <img src={docIcon} alt="" className="file-icon-image file-icon-image-doc" aria-hidden="true" />;
    }

    if (isPdfFile) {
      return <img src={pdfIcon} alt="" className="file-icon-image file-icon-image-pdf" aria-hidden="true" />;
    }

    return <i className={iconClass}></i>;
  };

  const handleCloseUploadModal = useCallback(() => {
    setPendingUploadFiles([]);
    setIsFileDropzoneActive(false);
    fileDropzoneDragDepthRef.current = 0;
    if (typeof onCloseUploadModal === 'function') {
      onCloseUploadModal();
    }
  }, [onCloseUploadModal]);

  if (loading) {
    return (
      <div className="file-explorer-loading">
        <div className="file-explorer-loading-spinner"></div>
        <span>Loading files...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="file-explorer-error">
        <i className="fas fa-exclamation-triangle"></i>
        <span>Error loading files: {error}</span>
        <button onClick={() => loadProjectFiles(true)} className="retry-btn">
          <i className="fas fa-refresh"></i>
          Retry
        </button>
      </div>
    );
  }

  const currentFiles = getCurrentFiles();
  const hasFileListFilter = String(effectiveFileListFilterText ?? '').trim().length > 0;
  const isCurrentFolderLoading = Boolean(currentFolder?.googleDriveId)
    && !currentFolder?.driveChildrenLoaded
    && (!Array.isArray(currentFolder?.children) || currentFolder.children.length === 0);

  const handleTreePanelResizeStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    treePanelResizeRef.current = { startX: e.clientX, startW: treePanelWidth };
    const onMove = (e2) => {
      const dx = e2.clientX - treePanelResizeRef.current.startX;
      const newW = treePanelResizeRef.current.startW + dx;
      setTreePanelWidth(Math.min(600, Math.max(300, newW)));
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  return (
    <div className={`file-explorer${hideTreePanel ? ' file-explorer-list-only' : ''}`}>
      <div className="file-explorer-content">
        {!hideTreePanel && (
          <>
        {/* Tree Panel - resizable via right edge (300–500px, default 350px) */}
        <div 
          className="tree-panel"
          style={{ width: treePanelWidth }}
          onClick={clearSelection}
        >
          <div className="file-tree-content">
            {fileTree
              .filter(item => item.isFolder)
              .map(item => renderTreeItem(item))}
          </div>
          {showDownloadNotification && (
            <div className={`download-notification ${isClosingNotification ? 'closing' : ''}`}>
              <div className="download-notification-content">
                <div className="download-notification-header">
                  {isMultipleFiles ? (
                    <i className={`fas ${downloadStatus === 'creating' ? 'fa-file-archive' : 'fa-download'}`}></i>
                  ) : (
                    <div className="download-loading-circle"></div>
                  )}
                  <span>
                    {isMultipleFiles && downloadStatus === 'creating' 
                      ? 'Creating ZIP...' 
                      : 'Downloading files...'}
                  </span>
                </div>
                {isMultipleFiles && (
                  <div className="download-progress-container">
                    <div className="download-progress-bar">
                      <div 
                        className="download-progress-fill" 
                        style={{ width: `${downloadProgress}%` }}
                      ></div>
                    </div>
                    <span className="download-progress-text">{Math.round(downloadProgress)}%</span>
                    {downloadStatus === 'creating' && (
                      <button 
                        className="download-cancel-btn"
                        onClick={handleCancelDownload}
                        title="Cancel ZIP"
                      >
                        <i className="fas fa-times"></i>
                        <span className="cancel-tooltip">Cancel ZIP</span>
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
          <div
            className="tree-panel-resize-handle"
            onMouseDown={handleTreePanelResizeStart}
            title="Drag to resize"
            aria-label="Resize tree panel"
          />
        </div>
          </>
        )}
        {hideTreePanel && showDownloadNotification && (
          <div className={`download-notification ${isClosingNotification ? 'closing' : ''}`}>
            <div className="download-notification-content">
              <div className="download-notification-header">
                {isMultipleFiles ? (
                  <i className={`fas ${downloadStatus === 'creating' ? 'fa-file-archive' : 'fa-download'}`}></i>
                ) : (
                  <div className="download-loading-circle"></div>
                )}
                <span>
                  {isMultipleFiles && downloadStatus === 'creating'
                    ? 'Creating ZIP...'
                    : 'Downloading files...'}
                </span>
              </div>
              {isMultipleFiles && (
                <div className="download-progress-container">
                  <div className="download-progress-bar">
                    <div
                      className="download-progress-fill"
                      style={{ width: `${downloadProgress}%` }}
                    ></div>
                  </div>
                  <span className="download-progress-text">{Math.round(downloadProgress)}%</span>
                  {downloadStatus === 'creating' && (
                    <button
                      className="download-cancel-btn"
                      onClick={handleCancelDownload}
                      title="Cancel ZIP"
                    >
                      <i className="fas fa-times"></i>
                      <span className="cancel-tooltip">Cancel ZIP</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* File List Panel */}
        <div className="file-list-panel">
          {fileListTopContent != null && (
            <div
              className="file-list-top-content"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {fileListTopContent}
            </div>
          )}
          {/* Mobile: the toolbar row exists ONLY while the search bar is open
              (the search icon lives in the Deliverables breadcrumb pill, and
              the selection count lives in the sortbar) — otherwise the row
              would sit empty. Desktop is unchanged.
              When the host hands us a portal node (the breadcrumb pill), the
              open bar renders INTO it as an overlay instead of as a flow row,
              so opening search never changes the page height. */}
          {(showFileListFilter || fileListToolbar != null || selectedFiles.length > 0) &&
            (!isMobile || mobileSearchOpen) && (() => {
            const overlayIntoPill =
              isMobile && mobileSearchOpen && mobileSearchPortalNode != null;
            const toolbar = (
            <div
              className={`file-list-toolbar${overlayIntoPill ? ' file-list-toolbar--overlay' : ''}`}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {!isMobile && selectedFiles.length > 0 && (
                <div className="file-list-toolbar-selection">
                  <button
                    type="button"
                    className="file-list-clear-selection-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      clearSelection();
                    }}
                    title="Clear selection"
                    aria-label="Clear selection"
                  >
                    <i className="fas fa-times" aria-hidden />
                  </button>
                  <span className="selected-count">
                    {getSelectedFilesCount()} selected
                  </span>
                </div>
              )}
              {!isMobile && fileListToolbar}
              {showFileListFilter && (!isMobile || mobileSearchOpen) && (
                <label className="file-list-toolbar-filter">
                  <i className="fas fa-search" aria-hidden />
                  <input
                    type="search"
                    value={effectiveFileListFilterText ?? ''}
                    onChange={(e) => handleFileListFilterChange(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    placeholder="Filter files and folders"
                    aria-label="Filter files and folders"
                    autoFocus={isMobile}
                  />
                </label>
              )}
              {showFileListFilter && isMobile && mobileSearchOpen && (
                <button
                  type="button"
                  className="file-list-search-close"
                  onClick={() => {
                    setMobileSearchOpen(false);
                    handleFileListFilterChange('');
                  }}
                  aria-label="Close search"
                >
                  <i className="fas fa-times" aria-hidden />
                </button>
              )}
            </div>
            );
            return overlayIntoPill
              ? createPortal(toolbar, mobileSearchPortalNode)
              : toolbar;
          })()}
          {renderFileListHeader()}
          {/* Mobile sort/status bar. The sort-field <select> was dropped by
              design — the direction arrow (default: name order) is the only
              sort control left on phones. While a long-press selection is
              active, this row also carries the count + clear-X (moved here
              from the toolbar, which now only exists while search is open). */}
          {isMobile && (
            <div
              className="file-list-sortbar"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {mobileBackAvailable && onMobileBack && (
                <button
                  type="button"
                  className="file-list-sortbar-back"
                  aria-label="Back to previous folder"
                  onClick={(e) => {
                    e.stopPropagation();
                    onMobileBack();
                  }}
                >
                  <i className="fas fa-arrow-left" aria-hidden />
                </button>
              )}
              {selectedFiles.length > 0 && (
                <div className="file-list-toolbar-selection">
                  <button
                    type="button"
                    className="file-list-clear-selection-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      clearSelection();
                    }}
                    title="Clear selection"
                    aria-label="Clear selection"
                  >
                    <i className="fas fa-times" aria-hidden />
                  </button>
                  <span className="selected-count">
                    {getSelectedFilesCount()} selected
                  </span>
                </div>
              )}
              {/* Selection actions, mirroring the desktop per-row buttons:
                  Delete for any selection, Rename only when it is exactly
                  one item (inline rename edits a single row). Both drive
                  the same handlers the desktop buttons use. */}
              {selectedFiles.length > 0 && (
                <div className="file-list-sortbar-actions">
                  <button
                    type="button"
                    className="file-list-sortbar-action"
                    title="Download"
                    aria-label="Download selected"
                    onClick={(e) => {
                      e.stopPropagation();
                      // Bare call = the selection path, same as the
                      // imperative handle's handleDownload.
                      handleDownload();
                    }}
                  >
                    <i className="fas fa-download" aria-hidden />
                  </button>
                  {selectedFiles.length === 1 && (
                    <button
                      type="button"
                      className="file-list-sortbar-action"
                      title="Rename"
                      aria-label="Rename"
                      onClick={(e) => {
                        e.stopPropagation();
                        startInlineRename(selectedFiles[0]);
                      }}
                    >
                      <i className="fas fa-pen" aria-hidden />
                    </button>
                  )}
                  <button
                    type="button"
                    className="file-list-sortbar-action"
                    title="Delete"
                    aria-label="Delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowDeleteModal(true);
                    }}
                  >
                    <i className="fas fa-trash" aria-hidden />
                  </button>
                </div>
              )}
              <button
                type="button"
                className="file-list-sortbar-dir"
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
                aria-label={
                  sortOrder === 'asc'
                    ? 'Sorted ascending, tap to sort descending'
                    : 'Sorted descending, tap to sort ascending'
                }
              >
                <i
                  className={`fa-solid ${sortOrder === 'asc' ? 'fa-arrow-up-wide-short' : 'fa-arrow-down-short-wide'}`}
                  aria-hidden="true"
                />
              </button>
            </div>
          )}
          <div
            className="file-list-content"
            ref={fileListRef}
            onMouseDown={handleFileListMarqueeMouseDown}
            onDragEnter={handleFileListDragEnter}
            onDragOver={handleFileListDragOver}
            onDragLeave={handleFileListDragLeave}
            onDrop={handleFileListDrop}
            onContextMenu={(e) => handleRightClick(e)}
            onClick={handleFileListContentClick}
          >
            {isFileDropzoneActive && (
              <div className="upload-dropzone file-list-content-upload-overlay" aria-hidden>
                <div className="upload-file-label file-list-content-upload-label">
                  <i className="fas fa-cloud-upload-alt" aria-hidden />
                  <span>Drop files or folders to upload</span>
                </div>
              </div>
            )}
            {marqueeRect && (marqueeRect.width > 0 || marqueeRect.height > 0) && (
              <div
                className="file-list-marquee-selection"
                aria-hidden
                style={{
                  left: marqueeRect.left,
                  top: marqueeRect.top,
                  width: marqueeRect.width,
                  height: marqueeRect.height,
                }}
              />
            )}
            {isCurrentFolderLoading ? (
              <div className="empty-folder empty-folder-loading">
                <div className="file-explorer-loading-spinner"></div>
                <span>Loading folder contents...</span>
              </div>
            ) : currentFiles.length === 0 ? (
              <div className="empty-folder">
                <i className={`fas ${hasFileListFilter ? 'fa-search' : 'fa-folder-open'}`}></i>
                <span>
                  {hasFileListFilter
                    ? 'No files or folders match this filter'
                    : 'This folder is empty'}
                </span>
              </div>
            ) : (
              <>
                {creatingFolder && (
                  <div className="file-list-item file-list-item-even folder-item creating-folder-row">
                    <div className="file-icon" style={{ width: `${columnWidths.icon}px` }}>
                      <i className="fas fa-folder" aria-hidden />
                    </div>
                    <div className="file-name" style={{ flex: 1 }}>
                      <div className="rename-input-wrapper">
                        <input
                          ref={newFolderInputRef}
                          type="text"
                          className={`file-name-text file-name-input ${!newFolderValidation.ok ? 'has-conflict' : ''}`}
                          value={newFolderDraft}
                          autoFocus
                          onChange={(e) => setNewFolderDraft(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          onDoubleClick={(e) => e.stopPropagation()}
                          onContextMenu={(e) => e.stopPropagation()}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.code === 'Enter' || e.code === 'NumpadEnter') {
                              e.preventDefault();
                              e.stopPropagation();
                              void commitNewFolder();
                              return;
                            }
                            if (e.key === 'Escape') {
                              e.preventDefault();
                              e.stopPropagation();
                              cancelNewFolder();
                            }
                          }}
                          onBlur={() => {
                            // Blur with a colliding name discards the placeholder (matches Windows
                            // Explorer's "click-away discards a new folder" behavior). Otherwise
                            // commit normally.
                            if (!newFolderValidation.ok) {
                              cancelNewFolder();
                              return;
                            }
                            void commitNewFolder();
                          }}
                        />
                        {!newFolderValidation.ok && (
                          <div className="rename-conflict-banner">
                            <span>A folder named "{newFolderDraft.trim()}" already exists.</span>
                            <button
                              type="button"
                              className="rename-conflict-suggest"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => setNewFolderDraft(newFolderValidation.suggested)}
                            >
                              Use "{newFolderValidation.suggested}" instead
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                {currentFiles.map((item, index) => renderFileListItem(item, index))}
              </>
            )}
          </div>
          {/* Footer */}
          <div className="file-explorer-footer">
            <div className="footer-content">
              <span className="total-files">
                {currentFiles.length} item{currentFiles.length !== 1 ? 's' : ''}
              </span>
            </div>
            {footerExtra && (
              <div className="file-explorer-footer-center">
                {footerExtra}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Context Menu */}
      <ContextMenu
        visible={contextMenu.visible}
        position={contextMenu.position}
        onClose={closeContextMenu}
        onRightClickOnBackdrop={(e) => {
          // When right-clicking on backdrop while menu is visible:
          // Close old menu and open new one at cursor position
          // The menu will open with currently selected files (or empty if clicking on empty space)
          closeContextMenu();
          
          // Use setTimeout to ensure state update completes before opening new menu
          setTimeout(() => {
            // Open menu at new position
            // If clicking on a file item, that item's handler would have fired first (before backdrop)
            // Since backdrop fires, we know it's empty space, so pass null for item
            openContextMenuAtPosition(e, null);
          }, 0);
        }}
        selectedFiles={selectedFiles}
        onCopy={handleCopy}
        onPaste={handlePaste}
        onDelete={handleDelete}
        onRename={handleRename}
        onDownload={handleDownload}
        onCut={handleCut}
        onSelectAll={selectAll}
        onRefresh={handleRefresh}
        canPaste={clipboard.files.length > 0 && !!currentFolder}
        canRename={selectedFiles.length === 1}
        canDelete={selectedFiles.length > 0 && !deleteBlockedForSourceSubtreeFolders}
        canDownload={selectedFiles.length > 0}
      />
      
      {/* Upload Modal */}
      <UploadModal
        isOpen={showUploadModal}
        onClose={handleCloseUploadModal}
        onUpload={() => loadProjectFiles(true)}
        onFileAdded={handleFileAdded}
        initialFiles={pendingUploadFiles}
        projectId={project?.Id}
        projectType={project?.ProjectType}
        currentFolderId={/* the folder the list is actually rendering:
          getCurrentFiles() only ever returns currentFolder.children, so an upload
          aimed elsewhere lands where the explorer cannot display it. ProjectView's
          currentFolderId falls back to project.Id when no folder is explicitly
          selected, and rows with parent_id == projects_id are root-level siblings
          of the tree's first folder -- present in fileTree, never rendered. */
          currentFolder?.id || currentFolderId || project?.Id}
        currentFolderGoogleDriveId={currentFolder?.googleDriveId || null}
        currentFolderItems={currentFolder?.children || []}
        projectName={project?.Name}
        currentFolderName={currentFolder && currentFolder.id !== project?.Id ? currentFolder.name : null}
      />
      
      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        items={selectedFiles}
      />
      
      {/* Video Player Modal */}
      <VideoPlayerModal
        isOpen={showVideoModal}
        onClose={() => {
          setShowVideoModal(false);
          setSelectedVideoFile(null);
        }}
        file={selectedVideoFile}
      />
      
      {/* Image Player Modal */}
      <ImagePlayerModal
        isOpen={showImageModal}
        onClose={() => {
          setShowImageModal(false);
          setSelectedImageFile(null);
        }}
        file={selectedImageFile}
      />
      <DocumentViewerModal
        isOpen={showDocModal}
        onClose={() => {
          setShowDocModal(false);
          setSelectedDocFile(null);
        }}
        file={selectedDocFile}
        // Same project-name prefix a download of this item gets, so a PDF saved
        // from the viewer's own Save button is named like a downloaded one.
        downloadNamePrefix={selectedDocFile ? resolveDownloadNamePrefix([selectedDocFile]) : ''}
      />
    </div>
  );
});

FileExplorer.displayName = 'FileExplorer';

export default FileExplorer;
